
(function() {
    /**
     * newAbilities.js — Extended ability system for new card abilities.
     *
     * Extends window.abilities with:
     *   abilities.move   — existing hook, fires on every attack (before damage)
     *   abilities.onCrit — NEW hook, fires when attacker lands a confirmed critical hit
     *
     * battleArena.js must call the onCrit dispatcher after crit is confirmed:
     *
     *   if (critical && window.abilities?.onCrit?.[`${card1.abilityKey}`]) {
     *       abilities.onCrit[`${card1.abilityKey}`](cards1, cards2, index1, index2, c.model.events);
     *   }
     *
     * Function signature (all hooks):
     *   (team1, team2, index1, index2, events)
     *   team1[index1] = the attacker (card using the ability)
     *   team2[index2] = the current defender / target
     */

    if (typeof window.abilities === 'undefined') {
        console.warn('newAbilities.js: window.abilities not found. Load abilities.js first.');
        return;
    }

    // ── Initialize new hook sections if not already present ───────────────────
    if (!window.abilities.onCrit) {
        window.abilities.onCrit = {};
    }

    // onMissed — fires on the defender when the attacker misses them.
    // battleArena.js calls: abilities.onMissed[card2.abilityKey](cards2, cards1, index2, index1, events)
    //   team1[index1] = the defender (the card that was targeted but not hit)
    if (!window.abilities.onMissed) {
        window.abilities.onMissed = {};
    }

    // onHit — fires on the attacker after a successful hit, with the actual damage dealt.
    // battleArena.js calls: abilities.onHit[card1.abilityKey](cards1, cards2, index1, index2, damage, critical, events, logFn)
    //   team1[index1] = the attacker (the card that dealt the damage)
    //   damage        = the actual damage dealt this hit
    if (!window.abilities.onHit) {
        window.abilities.onHit = {};
    }

    // onDefeated — fires on the DEFENDER when they are killed (hp reaches 0).
    // battleArena.js calls: abilities.onDefeated[card2.abilityKey](cards2, cards1, index2, index1, excessDamage, events, logFn)
    //   team1[index1] = the defeated card (the one that just died)
    //   team2[index2] = the attacker (the card that landed the killing blow)
    //   excessDamage  = damage beyond the defender's remaining HP before this hit
    if (!window.abilities.onDefeated) {
        window.abilities.onDefeated = {};
    }

    // onKill — fires on the attacker when they defeat an opponent (defender hp <= 0).
    // battleArena.js calls: abilities.onKill[card1.abilityKey](cards1, cards2, index1, index2, events, logFn)
    //   team1[index1] = the attacker (the card that landed the killing blow)
    if (!window.abilities.onKill) {
        window.abilities.onKill = {};
    }

    // onMiss — fires on the attacker when their attack misses.
    // battleArena.js calls: abilities.onMiss[card1.abilityKey](cards1, cards2, index1, index2, events, logFn)
    //   team1[index1] = the attacker (the card that missed)
    //   team2[index2] = the defender (the intended target)
    if (!window.abilities.onMiss) {
        window.abilities.onMiss = {};
    }

    // teamCrit — fires on the card waiting immediately BEHIND the one that just
    // took a critical hit (index + 1 in the team array).
    // battleArena.js calls: abilities.teamCrit[supportCard.abilityKey](team, attackerTeam, supportIndex, attackerIndex, events)
    //   team1[index1] = the support card (e.g. Anko) reacting to her teammate's crit
    if (!window.abilities.teamCrit) {
        window.abilities.teamCrit = {};
    }

    // passive — fires for every alive card on BOTH teams before each attack,
    // regardless of whose turn it is.
    // battleArena.js calls: abilities.passive[card.abilityKey](card, cardIndex, team, opposingTeam, opposingIndex)
    //   card      = the card whose passive is firing
    //   cardIndex = that card's position in its own team array
    //   team      = the full team array the card belongs to
    // Uses card._passiveApplied[abilityKey] flag to apply one-time effects only once.
    if (!window.abilities.passive) {
        window.abilities.passive = {};
    }

    // ==========================================================================
    // STAT GLOW HELPER — visual feedback when an ability modifies a stat
    // ==========================================================================
    /**
     * flashStatGlow(card, statName, origVal, newVal)
     * Targets the [data-stat="statName"] <p> on the card's DOM element and
     * applies a green glow (boost) or red glow (drain) for 2.5 seconds.
     * If the boost is x3 or more of the original value, a 🔥 icon is appended.
     *
     * stat names: 'pAtk', 'mAtk', 'armor', 'ward', 'critChance', 'critDmg'
     */
    window.flashStatGlow = function(card, statName, origVal, newVal) {
        if (typeof $ === 'undefined') { return; }
        var cardEl = document.getElementById(card.id);
        if (!cardEl) { return; }
        var pEl = cardEl.querySelector('[data-stat="' + statName + '"]');
        if (!pEl) { return; }

        var isBoost = newVal > origVal;
        var glowClass = isBoost ? 'stat-glow-boost' : 'stat-glow-drain';

        pEl.classList.remove('stat-glow-boost', 'stat-glow-drain', 'stat-overkill');
        var existingFire = pEl.querySelector('.stat-fire-icon');
        if (existingFire) { existingFire.remove(); }

        pEl.classList.add(glowClass);

        // x3 or more boost = overkill → fire icon
        if (isBoost && origVal > 0 && newVal >= origVal * 3) {
            pEl.classList.add('stat-overkill');
            var fire = document.createElement('span');
            fire.className = 'stat-fire-icon';
            fire.textContent = '🔥';
            pEl.appendChild(fire);
        }

        setTimeout(function() {
            pEl.classList.remove('stat-glow-boost', 'stat-glow-drain', 'stat-overkill');
            var fi = pEl.querySelector('.stat-fire-icon');
            if (fi) { fi.remove(); }
        }, 2500);
    };

    // ==========================================================================
    // PASSIVE abilities (fire every turn for ALL alive cards, both teams)
    // Signature: (card, cardIndex, team, opposingTeam, opposingIndex)
    //   card      = the card whose passive is active
    //   cardIndex = card's position in its own team array
    //   team      = the card's own team array
    // ==========================================================================

    /**
     * Anko — Iron Presence (Standard / Passive)
     * Card:    Anko  |  D82EC6D0-D750-4F93-96C4-FC283B1F70AD
     * Ability: A03B144B-8D10-4C6F-AB3C-744A4C32486A
     *
     * As long as Anko exists on the team (hp > 0), the card directly in front
     * of her (index + 1) gains +25 armor every turn — stacks each turn Anko
     * is alive. Fires regardless of whose turn it is.
     */
    window.abilities.passive['A03B144B-8D10-4C6F-AB3C-744A4C32486A'] = function(card, cardIndex, team, opposingTeam, opposingIndex, events, logFn) {
        const frontCard = team[cardIndex - 1];
        if (!frontCard || frontCard.hp <= 0) { return; }

        if (!frontCard._passiveApplied) { frontCard._passiveApplied = {}; }
        if (frontCard._passiveApplied['A03B144B-8D10-4C6F-AB3C-744A4C32486A']) { return; }

        var _origFrontArmor = Number(frontCard.armor) || Number(frontCard.defence) || 0;
        frontCard.armor = _origFrontArmor + 25;
        frontCard._passiveApplied['A03B144B-8D10-4C6F-AB3C-744A4C32486A'] = true;
        window.flashStatGlow(frontCard, 'armor', _origFrontArmor, frontCard.armor);
        effects.add(frontCard, ['glow-green']);

        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Iron Presence granted ${frontCard.name} +25 armor (now ${frontCard.armor})`, 'ability', {
                card: card.name,
                ability: card.ability,
                target: frontCard.name,
                armorAdded: 25,
                totalArmor: frontCard.armor,
                tier: card.tier
            });
        }
    };

    // ==========================================================================
    // TEAM-CRIT abilities (fire on the card BEHIND the one that took a critical hit)
    // team1[index1] = the reacting support card
    // ==========================================================================

    /**
     * Anko — Iron Presence (Variant)
     * Card:    Anko  |  D82EC6D0-D750-4F93-96C4-FC283B1F70AD
     * Ability: A03B144B-8D10-4C6F-AB3C-744A4C32486A
     *
     * Every time the teammate directly in front of Anko takes a critical hit,
     * Anko gains +800 attack (pAtk). Stacks each time it triggers.
     */
    window.abilities.teamCrit['A03B144B-8D10-4C6F-AB3C-744A4C32486A'] = function(team1, team2, index1, index2, events) {
        const anko = team1[index1];
        if (!anko || anko.hp <= 0) { return; }

        var _origAnkoPAtk = Number(anko.pAtk) || Number(anko.attack) || 0;
        anko.pAtk   = _origAnkoPAtk + 800;
        anko.attack = (Number(anko.attack)  || 0) + 800;
        window.flashStatGlow(anko, 'pAtk', _origAnkoPAtk, anko.pAtk);
        effects.add(anko, ['powerup']);
    };

    // ==========================================================================
    // MOVE abilities (fire on every attack, before damage is applied)
    // ==========================================================================

    /**
     * Acez — Stamina Drain (Standard)
     * Card:    Acez  |  7B824FDE-C4C8-41CC-9515-603AF8F6BAA7
     * Ability: 70B46465-EEEE-44AB-9C4E-BC19EC788DB3
     *
     * Each hit against the opponent has a 50% chance to drain 5 stamina.
     */
    window.abilities.move['70B46465-EEEE-44AB-9C4E-BC19EC788DB3'] = function(team1, team2, index1, index2, events) {
        const card2 = team2[index2];

        if (abilities.modifiers.card(card2)) { return; }

        if (Math.random() < 0.50) {
            if (card2._throttleStaminaImmune) {
                card2._throttleStaminaBlockedThisTick = true;
                effects.add(card2, ['powerup']);
            } else {
                card2.currentStamina = Math.max(0, (Number(card2.currentStamina) || 0) - 5);
                effects.add(card2, ['shakeY']);
            }
        }
    };

    // ==========================================================================
    // DAMAGED abilities (fire when THIS card takes damage)
    // Signature from battleArena: (team1, team2, index1, index2, damage, events)
    //   team1[index1] = the card that was hit (the ability owner)
    //   team2[index2] = the attacker
    // ==========================================================================

    /**
     * Akita — Energy Break (Standard + Variant)
     * Card:    Akita  |  504E8AD0-A0B0-4A9C-B6D1-76CCB2935F2A
     * Ability: DCEBAAC0-824C-4EC7-BF42-2C7A9922D169
     *
     * Standard: 20% chance after taking damage to add 500 physical attack (pAtk).
     * Variant:  10% chance after taking damage to add 1000 magic attack (mAtk).
     * Both rolls are independent and can both proc on the same hit.
     */
    window.abilities.damaged['DCEBAAC0-824C-4EC7-BF42-2C7A9922D169'] = function(team1, team2, index1, index2, damage, events) {
        const card = team1[index1];

        // Standard — 20% chance: +500 pAtk
        if (Math.random() < 0.20) {
            var _origAkitaPAtk = Number(card.pAtk) || Number(card.attack) || 0;
            card.pAtk = _origAkitaPAtk + 500;
            window.flashStatGlow(card, 'pAtk', _origAkitaPAtk, card.pAtk);
            effects.add(card, ['glow-green']);
        }

        // Variant — 10% chance: +1000 mAtk
        if (Math.random() < 0.10) {
            var _origAkitaMAtk = Number(card.mAtk) || 0;
            card.mAtk = _origAkitaMAtk + 1000;
            window.flashStatGlow(card, 'mAtk', _origAkitaMAtk, card.mAtk);
            effects.add(card, ['powerup']);
        }
    };

    // ==========================================================================
    // ON-CRIT abilities (fire only when a confirmed critical hit lands)
    // ==========================================================================

    /**
     * Acez — Stamina Drain (Variant)
     * Card:    Acez  |  7B824FDE-C4C8-41CC-9515-603AF8F6BAA7
     * Ability: 70B46465-EEEE-44AB-9C4E-BC19EC788DB3
     *
     * Critical hits against the opponent drain 15 stamina (guaranteed, no roll).
     * Stacks on top of the standard 50% drain if both trigger on the same hit.
     */
    window.abilities.onCrit['70B46465-EEEE-44AB-9C4E-BC19EC788DB3'] = function(team1, team2, index1, index2, events) {
        const card2 = team2[index2];

        if (abilities.modifiers.card(card2)) { return; }

        if (card2._throttleStaminaImmune) {
            card2._throttleStaminaBlockedThisTick = true;
            effects.add(card2, ['powerup']);
        } else {
            card2.currentStamina = Math.max(0, (Number(card2.currentStamina) || 0) - 15);
            effects.add(card2, ['glow-red']);
        }
    };

    // ==========================================================================
    // CALLOWAY abilities
    // ==========================================================================

    /**
     * Calloway — Victory Surge (Standard + Variant)
     * Card:    Calloway  |  DC65324F-BC45-4233-A403-BBE2C95AD114
     * Ability: B71A04A1-3082-43E1-8B0C-5B267BA139FF
     *
     * Standard: When Calloway defeats an opponent, stamina and mana are
     *           fully restored to max.
     *
     * Variant:  When Calloway defeats an opponent, HP begins regenerating
     *           +400 per turn for the rest of the battle.
     */
    window.abilities.onKill['B71A04A1-3082-43E1-8B0C-5B267BA139FF'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1]; // Calloway

        // Standard — fully restore stamina and mana
        card.currentStamina = card.maxStamina || card.stamina || 100;
        card.currentMana    = card.maxMana    || card.mana    || 100;
        effects.add(card, ['powerup']);

        // Variant — activate HP regen for the rest of the battle
        card._hpRegenPerTurn = (card._hpRegenPerTurn || 0) + 400;
        effects.add(card, ['glow-green']);

        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Victory Surge — stamina & mana fully restored, +400 HP regen per turn activated`, 'ability', {
                card: card.name, ability: 'Victory Surge', hpRegenPerTurn: card._hpRegenPerTurn, tier: card.tier
            });
        }
    };

    window.abilities.passive['B71A04A1-3082-43E1-8B0C-5B267BA139FF'] = function(card) {
        if (!card._hpRegenPerTurn) { return; }
        const maxHp = card._maxHp || card.hp;
        card.hp = Math.min(maxHp, (Number(card.hp) || 0) + card._hpRegenPerTurn);
        effects.add(card, ['glow-green']);
    };

    // ==========================================================================
    // BUCHE abilities
    // ==========================================================================

    /**
     * Buche — Soul Drain (Standard + Variant)
     * Card:    Buche  |  7FF5205E-A176-4347-9129-D89E1557DBA6
     * Ability: E64BF6D8-45F6-46F0-8D83-792B223233FB
     *
     * Standard: 90% chance on each attack to absorb the opponent's mana cost
     *           (calculated as their normal attack mana cost) and add it to Buche's mana.
     *
     * Variant:  90% chance on each attack to absorb the opponent's stamina cost
     *           and add it to Buche's stamina.
     *
     * Both rolls are independent.
     * Resource cost formula mirrors battleArena.js: clamp(stat * 0.12, 8, 20)
     */
    window.abilities.move['E64BF6D8-45F6-46F0-8D83-792B223233FB'] = function(team1, team2, index1, index2, _events) {
        const card     = team1[index1]; // Buche
        const opponent = team2[index2];

        if (abilities.modifiers.card(opponent)) { return; }

        const manaCost    = Number(opponent.mAtk) > 0
            ? Math.max(8, Math.min(20, Math.round(Number(opponent.mAtk)    * 0.12))) : 0;
        const staminaCost = Number(opponent.pAtk) > 0
            ? Math.max(8, Math.min(20, Math.round(Number(opponent.pAtk)    * 0.12))) : 0;

        // Standard — 90% chance: absorb opponent mana cost
        if (manaCost > 0 && Math.random() < 0.40) {
            const absorbed = Math.min(manaCost, Number(opponent.currentMana) || 0);
            opponent.currentMana = Math.max(0, (Number(opponent.currentMana) || 0) - absorbed);
            card.currentMana     = Math.min(card.maxMana || 100, (Number(card.currentMana) || 0) + absorbed);
            effects.add(card, ['glow-blue']);
        }

        // Variant — 90% chance: absorb opponent stamina cost
        if (staminaCost > 0 && Math.random() < 0.40) {
            if (opponent._throttleStaminaImmune) {
                opponent._throttleStaminaBlockedThisTick = true;
                effects.add(opponent, ['powerup']);
            } else {
                const absorbed = Math.min(staminaCost, Number(opponent.currentStamina) || 0);
                opponent.currentStamina = Math.max(0, (Number(opponent.currentStamina) || 0) - absorbed);
                card.currentStamina     = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + absorbed);
                effects.add(card, ['powerup']);
            }
        }
    };

    // ==========================================================================
    // BRINX abilities
    // ==========================================================================

    /**
     * Brinx — Final Stand (Standard + Variant)
     * Card:    Brinx  |  F0725709-2409-4B55-A9EC-52874B417B69
     * Ability: 0A7709D2-DF2B-4E22-9BFC-290AE79E2E84
     *
     * Standard: When the OPPONENT's HP is below 15%, their non-crit attacks on
     *           Brinx deal no damage (reversed). Only opponent crits can hurt Brinx.
     *
     * Variant:  When the opponent's HP is below 15% and they land a critical hit
     *           on Brinx, Brinx recharges 1200 HP.
     *
     * Passive initialises _maxHp for all cards in both teams on first encounter.
     */
    window.abilities.passive['0A7709D2-DF2B-4E22-9BFC-290AE79E2E84'] = function(_card, _cardIndex, team, opposingTeam) {
        for (const c of team)         { if (c && !c._maxHp) { c._maxHp = c.hp; } }
        for (const c of opposingTeam) { if (c && !c._maxHp) { c._maxHp = c.hp; } }
    };

    // Standard + Variant — both fire when Brinx takes damage
    window.abilities.damaged['0A7709D2-DF2B-4E22-9BFC-290AE79E2E84'] = function(team1, team2, index1, index2, damage, _events, critical) {
        const card     = team1[index1]; // Brinx (damaged card)
        const attacker = team2[index2]; // opponent (who hit Brinx)

        // Require attacker _maxHp to be set; skip if not yet available
        if (!attacker._maxHp) { return; }

        const isBelow15 = attacker.hp < attacker._maxHp * 0.15;
        if (!isBelow15) { return; }

        if (!critical) {
            // Standard — reverse damage, opponent non-crits don't land
            card.hp = Math.min(card._maxHp || card.hp + damage, (Number(card.hp) || 0) + damage);
            effects.add(card, ['glow-blue']);
        } else {
            // Variant — opponent crit from below 15% recharges Brinx
            card.hp = Math.min(card._maxHp || card.hp, (Number(card.hp) || 0) + 1200);
            effects.add(card, ['glow-green']);
        }
    };

    // ==========================================================================
    // BRAIL abilities
    // ==========================================================================

    /**
     * Brail — Inferno Sync (Single ability, two burning-gated effects)
     * Card:    Brail  |  83C15072-0AC5-4215-BB38-24B4E9685D96
     * Ability: DFC8F65E-A20D-4F11-BA7C-F77560D4B213
     *
     * Both effects only activate while Brail is burning.
     *
     * onCrit:  When Brail is burning and lands a critical hit, the opponent
     *          automatically gets burned.
     *
     * onHit:   When Brail is burning, 35% chance to deal a secondary magic
     *          attack (mAtk vs opponent ward) on the same turn.
     */
    const _isBurning = (card) =>
        !!(card.activeTraits && card.activeTraits.burning && card.activeTraits.burning.turnsLeft > 0);

    // Fires when BRAIL crits the opponent while burning → burn the opponent
    window.abilities.onCrit['DFC8F65E-A20D-4F11-BA7C-F77560D4B213'] = function(team1, team2, index1, index2, _events) {
        const card     = team1[index1]; // Brail
        const opponent = team2[index2];
        if (!_isBurning(card)) { return; }
        if (!opponent || opponent.hp <= 0) { return; }

        if (window.traits) { traits.apply(opponent, 'burning'); }
        effects.add(opponent, ['glow-red']);
    };

    // Fires when OPPONENT crits Brail while she is burning → burn the opponent
    window.abilities.damaged['DFC8F65E-A20D-4F11-BA7C-F77560D4B213'] = function(team1, team2, index1, index2, _damage, _events, critical) {
        if (!critical) { return; }
        const card     = team1[index1]; // Brail (the one who took the hit)
        const attacker = team2[index2]; // opponent who landed the crit
        if (!_isBurning(card)) { return; }
        if (!attacker || attacker.hp <= 0) { return; }

        if (window.traits) { traits.apply(attacker, 'burning'); }
        effects.add(attacker, ['glow-red']);
    };

    window.abilities.onHit['DFC8F65E-A20D-4F11-BA7C-F77560D4B213'] = function(team1, team2, index1, index2, _damage, _critical, _events, logFn) {
        const card     = team1[index1]; // Brail
        const opponent = team2[index2];
        if (!_isBurning(card)) { return; }
        if (!opponent || opponent.hp <= 0) { return; }
        if (Math.random() >= 0.35) { return; }

        const secondaryDmg = Math.max(1, Math.round((Number(card.mAtk) || 0) - (Number(opponent.ward) || 0)));
        opponent.hp = Math.max(0, (Number(opponent.hp) || 0) - secondaryDmg);
        effects.add(opponent, ['glow-red']);
        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Inferno Sync triggered a secondary attack for ${secondaryDmg} damage!`, 'ability', {
                card: card.name, ability: 'Inferno Sync',
                secondaryDamage: secondaryDmg, tier: card.tier
            });
        }
    };

    // ==========================================================================
    // BOSCHE abilities
    // ==========================================================================

    /**
     * Bosche — Crit Mirror (Standard + Variant)
     * Card:    Bosche  |  F02BD5B0-6D1F-42A6-8E41-B7CEDC363E4D
     * Ability: 441B6E18-CB42-4624-A263-2E788BEB0AFC
     *
     * The roll chance equals the attacker's CURRENT crit chance (including any
     * buffs — e.g. Boltz's Sharp Miss stacks count toward this roll).
     *
     * Standard: On successful roll, deal 20% of the incoming damage back to the attacker.
     * Variant:  On successful roll, Bosche gains +500 pAtk. Stacks up to 3 times.
     */
    const _updateBoscheBadge = (card) => {
        if (typeof $ === 'undefined') { return; }
        const stacks = card._pAtkStacks || 0;
        const MAX = 3;
        const pips = Array.from({ length: MAX }, (_, i) =>
            `<span class="stack-pip${i < stacks ? ' active' : ''}"></span>`
        ).join('');
        const $card = $(`#${card.id}`);
        if (!$card.length) { return; }
        let $badge = $card.find('.ability-stack-badge');
        if (!$badge.length) {
            $badge = $('<div class="ability-stack-badge"></div>').appendTo($card);
        }
        $badge.html(`<div class="stack-row"><span>pAtk</span><span class="stack-pips">${pips}</span></div>`);
    };

    window.abilities.damaged['441B6E18-CB42-4624-A263-2E788BEB0AFC'] = function(team1, team2, index1, index2, damage, _events, _critical) {
        const card     = team1[index1]; // Bosche
        const attacker = team2[index2]; // opponent

        const opponentCritChance = Number(attacker.critChance) || Number(attacker.criticalChance) || 5;

        if (Math.random() * 100 >= opponentCritChance) { return; }

        // Standard — deal 20% of incoming damage back to attacker
        const reverseDmg = Math.round(damage * 0.20);
        attacker.hp = Math.max(0, (Number(attacker.hp) || 0) - reverseDmg);
        effects.add(attacker, ['glow-red']);

        // Variant — +500 pAtk to Bosche (max 3 stacks)
        if ((card._pAtkStacks || 0) < 3) {
            card._pAtkStacks = (card._pAtkStacks || 0) + 1;
            var _origBoschePAtk = Number(card.pAtk) || Number(card.attack) || 0;
            card.pAtk   = _origBoschePAtk + 500;
            card.attack = (Number(card.attack)  || 0) + 500;
            window.flashStatGlow(card, 'pAtk', _origBoschePAtk, card.pAtk);
            effects.add(card, ['powerup']);
            _updateBoscheBadge(card);
        }
    };

    // ==========================================================================
    // BOLTZ abilities
    // ==========================================================================

    /**
     * Boltz — Sharp Miss (Standard + Variant)
     * Card:    Boltz  |  21D066D7-19AD-45AE-9998-A2B681301A4A
     * Ability: 977CCED1-5B9E-42E8-A919-1399F7118291
     *
     * Standard: Each miss adds +50 to crit chance. Stacks up to 3 times.
     * Variant:  Each miss adds +30 to crit damage. Stacks up to 3 times.
     * Both stacks are tracked independently and shown as a pip indicator on the card.
     */
    const _updateBoltzBadge = (card) => {
        if (typeof $ === 'undefined') { return; }
        const critStacks = card._critChanceStacks || 0;
        const dmgStacks  = card._critDmgStacks    || 0;
        const MAX = 3;

        const pips = (count) => Array.from({ length: MAX }, (_, i) =>
            `<span class="stack-pip${i < count ? ' active' : ''}"></span>`
        ).join('');

        const html =
            `<div class="stack-row"><span>CRIT%</span><span class="stack-pips">${pips(critStacks)}</span></div>` +
            `<div class="stack-row"><span>CRIT×</span><span class="stack-pips">${pips(dmgStacks)}</span></div>`;

        const $card = $(`#${card.id}`);
        if (!$card.length) { return; }
        let $badge = $card.find('.ability-stack-badge');
        if (!$badge.length) {
            $badge = $('<div class="ability-stack-badge"></div>').appendTo($card);
        }
        $badge.html(html);
    };

    window.abilities.onMiss['977CCED1-5B9E-42E8-A919-1399F7118291'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1]; // Boltz

        // Standard — +50 crit chance per miss, max 3 stacks
        if ((card._critChanceStacks || 0) < 3) {
            card._critChanceStacks = (card._critChanceStacks || 0) + 1;
            var _origBoltzCrit = Number(card.critChance) || 0;
            card.critChance = _origBoltzCrit + 50;
            window.flashStatGlow(card, 'critChance', _origBoltzCrit, card.critChance);
            effects.add(card, ['powerup']);
        }

        // Variant — +30 crit damage per miss, max 3 stacks
        if ((card._critDmgStacks || 0) < 3) {
            card._critDmgStacks = (card._critDmgStacks || 0) + 1;
            card.critDamage = (Number(card.critDamage) || 0) + 30;
        }

        _updateBoltzBadge(card);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name} missed — Crit Chance stack ${card._critChanceStacks}/3 (+50 each), Crit Damage stack ${card._critDmgStacks}/3 (+30 each)`,
                'ability',
                { card: card.name, ability: 'Sharp Miss', critChanceStacks: card._critChanceStacks, critDmgStacks: card._critDmgStacks, tier: card.tier }
            );
        }
    };

    // ==========================================================================
    // BEYOTA abilities
    // ==========================================================================

    /**
     * Beyota — Dark Resilience (Standard + Variant)
     * Card:    Beyota  |  BFA34B37-C6C6-458F-83BD-1E47A43C26E1
     * Ability: E116AEE5-E2EC-4F14-9B2F-74378176FB0A
     *
     * Standard: While Beyota has any active negative status effect, she gains
     *           +400 mAtk and +10 ward every turn.
     *
     * Variant:  While Beyota has any active negative status effect and lands a
     *           critical hit, she gains +1500 mAtk.
     */
    const _hasNegativeStatus = (card) =>
        card.activeTraits && Object.values(card.activeTraits).some(t => t.turnsLeft > 0);

    window.abilities.passive['E116AEE5-E2EC-4F14-9B2F-74378176FB0A'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (!_hasNegativeStatus(card)) { return; }

        var _origBeyotaMAtk = Number(card.mAtk) || 0;
        var _origBeyotaWard = Number(card.ward) || 0;
        card.mAtk = _origBeyotaMAtk + 400;
        card.ward = _origBeyotaWard + 10;
        window.flashStatGlow(card, 'mAtk', _origBeyotaMAtk, card.mAtk);
        window.flashStatGlow(card, 'ward', _origBeyotaWard, card.ward);
        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Dark Resilience gained +400 mAtk and +10 ward from negative status`, 'ability', {
                card: card.name, ability: 'Dark Resilience',
                matkAdded: 400, wardAdded: 10, tier: card.tier
            });
        }
    };

    window.abilities.onCrit['E116AEE5-E2EC-4F14-9B2F-74378176FB0A'] = function(team1, _team2, index1, _index2, _events) {
        const card = team1[index1];
        if (!_hasNegativeStatus(card)) { return; }

        var _origBeyotaCritMAtk = Number(card.mAtk) || 0;
        card.mAtk = _origBeyotaCritMAtk + 1500;
        window.flashStatGlow(card, 'mAtk', _origBeyotaCritMAtk, card.mAtk);
        effects.add(card, ['powerup']);
    };

    // ==========================================================================
    // BAPHOMET abilities
    // ==========================================================================

    /**
     * Baphomet — Cursed Miss (Standard + Variant)
     * Card:    Baphomet  |  EB78D64D-6118-45E0-A2D9-3A83FBE7851C
     * Ability: 5C7CD355-5F08-4D46-8FDB-D4EE72B3098B
     *
     * Standard: When Baphomet misses an attack, the opponent loses 500 HP.
     * Variant:  When Baphomet misses an attack, the opponent loses 400 pAtk.
     * Both effects fire on every miss — they are not rolled.
     */
    window.abilities.onMiss['5C7CD355-5F08-4D46-8FDB-D4EE72B3098B'] = function(team1, team2, index1, index2, events, logFn) {
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }

        // Standard — opponent loses 500 HP
        opponent.hp = Math.max(0, (Number(opponent.hp) || 0) - 500);
        effects.add(opponent, ['glow-red']);

        // Variant — opponent loses 400 pAtk
        var _origBaphometOppPAtk = Number(opponent.pAtk) || Number(opponent.attack) || 0;
        opponent.pAtk   = Math.max(0, _origBaphometOppPAtk - 400);
        opponent.attack = Math.max(0, (Number(opponent.attack)  || 0) - 400);
        window.flashStatGlow(opponent, 'pAtk', _origBaphometOppPAtk, opponent.pAtk);
        effects.add(opponent, ['shakeY']);

        if (typeof logFn === 'function') {
            logFn(`Baphomet missed but cursed ${opponent.name} — −500 HP, −400 pAtk`, 'ability', {
                card: 'Baphomet',
                ability: 'Cursed Miss',
                target: opponent.name,
                hpDrained: 500,
                pAtkDrained: 400,
                tier: team1[index1]?.tier
            });
        }
    };

    // ==========================================================================
    // ATHERIA abilities
    // ==========================================================================

    /**
     * Atheria — Stamina Leech (Standard + Variant)
     * Card:    Atheria  |  80DC2638-B1D1-47B5-938D-647BE660AC23
     * Ability: 8449A196-D1D2-417A-ACA9-4E8D726C0F43
     *
     * Standard: When Atheria is hit by a physical attacker, that attacker loses
     *           90% of their current stamina and their stamina regen is reduced
     *           to 20% of normal until the battle ends.
     *
     * Variant:  When the opponent lands a critical hit on Atheria, they
     *           immediately lose 30% of their current stamina.
     */
    window.abilities.damaged['8449A196-D1D2-417A-ACA9-4E8D726C0F43'] = function(team1, team2, index1, index2, damage, events, critical) {
        const attacker = team2[index2];

        const isPhysical = attacker.primaryAttackType === 'physical' ||
                           (Number(attacker.pAtk) || 0) >= (Number(attacker.mAtk) || 0);

        // Standard — physical attacker loses 90% of current stamina; regen reduced to 20%
        if (isPhysical && (Number(attacker.pAtk) || 0) > 0) {
            if (attacker._throttleStaminaImmune) {
                attacker._throttleStaminaBlockedThisTick = true;
                effects.add(attacker, ['powerup']);
            } else {
                const drain = Math.round((Number(attacker.currentStamina) || 0) * 0.30);
                attacker.currentStamina = Math.max(0, (Number(attacker.currentStamina) || 0) - drain);
                attacker._staminaRegenMult = 0.20;
                effects.add(attacker, ['shakeY']);
            }
        }

        // Variant — critical hit costs the attacker 30% of their current stamina
        if (critical) {
            if (attacker._throttleStaminaImmune) {
                attacker._throttleStaminaBlockedThisTick = true;
                effects.add(attacker, ['powerup']);
            } else {
                const critDrain = Math.round((Number(attacker.currentStamina) || 0) * 0.30);
                attacker.currentStamina = Math.max(0, (Number(attacker.currentStamina) || 0) - critDrain);
                effects.add(attacker, ['glow-red']);
            }
        }
    };

    // ==========================================================================
    // ARKIANA abilities
    // ==========================================================================

    /**
     * Arkiana — Transmute (Standard + Variant)
     * Card:    Arkiana  |  912D5D98-0D02-4A68-AC22-15D8804CD23E
     * Ability: 579798A3-24D8-485B-B6C8-F226B0D66C3C
     *
     * Standard: 30% chance when damage output exceeds 3000 to heal Arkiana for
     *           50% of the excess damage. Caps at 2 procs per battle.
     *
     * Variant:  30% chance when damage output exceeds 3000 to gain mAtk equal to
     *           50% of the excess damage. Caps at 2 procs per battle.
     *
     * Both rolls are independent and each has its own cap of 2.
     */
    window.abilities.onHit['579798A3-24D8-485B-B6C8-F226B0D66C3C'] = function(team1, team2, index1, index2, damage, _critical, _events, logFn) {
        const card = team1[index1]; // Arkiana

        if (damage <= 3000) { return; }

        const excess = damage - 3000;
        const value  = Math.round(excess * 0.5);

        // Standard — 30% chance: heal HP (caps at 2 procs)
        if ((card._transmuteHealCount || 0) < 2 && Math.random() < 0.30) {
            card._transmuteHealCount = (card._transmuteHealCount || 0) + 1;
            card.hp = (Number(card.hp) || 0) + value;
            effects.add(card, ['glow-green']);
            if (typeof logFn === 'function') {
                logFn(`${card.name}'s Transmute converted excess damage into ${value} HP (proc ${card._transmuteHealCount}/2)`, 'ability', {
                    card: card.name, ability: card.ability,
                    healAmount: value, excess, proc: card._transmuteHealCount, tier: card.tier
                });
            }
        }

        // Variant — 30% chance: gain mAtk (caps at 2 procs)
        if ((card._transmuteMatkCount || 0) < 2 && Math.random() < 0.30) {
            card._transmuteMatkCount = (card._transmuteMatkCount || 0) + 1;
            var _origArkianaMAtk = Number(card.mAtk) || 0;
            card.mAtk = _origArkianaMAtk + value;
            window.flashStatGlow(card, 'mAtk', _origArkianaMAtk, card.mAtk);
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(`${card.name}'s Transmute converted excess damage into +${value} mAtk (proc ${card._transmuteMatkCount}/2)`, 'ability', {
                    card: card.name, ability: card.ability,
                    matkAdded: value, excess, proc: card._transmuteMatkCount, tier: card.tier
                });
            }
        }
    };

    // ==========================================================================
    // ARCANOX abilities
    // ==========================================================================

    /**
     * Arcanox — Arcane Counter (Standard + Variant)
     * Card:    Arcanox  |  DCC3E6CA-5199-4569-AADB-AA4ECDD6DE77
     * Ability: 081D11A1-64B8-4F4E-8AB8-C2CC18EA47ED
     *
     * Variant:  Every time a predominantly magic attacker lands a hit, Arcanox
     *           gains +10 ward and +300 mAtk.
     *
     * Standard: Tracks consecutive magic hits. On the 3rd consecutive magic hit
     *           without a miss, deals 3000 magic damage back to the attacker.
     *           Streak resets after the bonus fires or when a non-magic hit lands.
     */
    window.abilities.damaged['081D11A1-64B8-4F4E-8AB8-C2CC18EA47ED'] = function(team1, team2, index1, index2, damage, events) {
        const card     = team1[index1]; // Arcanox
        const attacker = team2[index2]; // the opponent who hit

        const isMagic = attacker.primaryAttackType === 'magic' ||
                        (Number(attacker.mAtk) || 0) > (Number(attacker.pAtk) || 0);

        if (!isMagic) {
            card._magicHitStreak = 0;
            return;
        }

        // Variant — every magic hit: +10 ward, +300 mAtk
        var _origArcanoxWard = Number(card.ward) || 0;
        var _origArcanoxMAtk = Number(card.mAtk) || 0;
        card.ward = _origArcanoxWard + 10;
        card.mAtk = _origArcanoxMAtk + 300;
        window.flashStatGlow(card, 'ward', _origArcanoxWard, card.ward);
        window.flashStatGlow(card, 'mAtk', _origArcanoxMAtk, card.mAtk);
        effects.add(card, ['glow-blue']);

        // Standard — track consecutive magic hits
        card._magicHitStreak = (card._magicHitStreak || 0) + 1;

        if (card._magicHitStreak >= 3) {
            attacker.hp = Math.max(0, (Number(attacker.hp) || 0) - 3000);
            effects.add(attacker, ['glow-red']);
            card._magicHitStreak = 0;
        }
    };

    /**
     * Arcanox — Arcane Counter (onMissed — streak reset)
     * Resets the consecutive magic hit counter when the opponent misses.
     */
    window.abilities.onMissed['081D11A1-64B8-4F4E-8AB8-C2CC18EA47ED'] = function(team1, team2, index1, index2, events) {
        const card = team1[index1]; // Arcanox
        card._magicHitStreak = 0;
    };

    /**
     * Casper — Ghost Dodge
     * Standard (onMissed): when opponent misses Casper, gain +10 ward and +400 mAtk
     * Variant (onMiss):    when Casper misses the opponent, recharge +800 HP
     */
    window.abilities.onMissed['D5F65EC7-44E3-49B5-8BE4-4EBE73576B94'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1]; // Casper — the defender who was missed
        var _origCasperWard = Number(card.ward) || 0;
        var _origCasperMAtk = Number(card.mAtk) || 0;
        card.ward = _origCasperWard + 10;
        card.mAtk = _origCasperMAtk + 400;
        window.flashStatGlow(card, 'ward', _origCasperWard, card.ward);
        window.flashStatGlow(card, 'mAtk', _origCasperMAtk, card.mAtk);
        effects.add(card, ['glow-blue']);
        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Ghost Dodge activated — gained +10 ward and +400 mAtk!`, 'ability', { cardId: card.id });
        }
    };

    window.abilities.onMiss['D5F65EC7-44E3-49B5-8BE4-4EBE73576B94'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1]; // Casper — the attacker who missed
        card.hp = (Number(card.hp) || 0) + 800;
        effects.add(card, ['glow-green']);
        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Ghost Dodge recharged 800 HP from a missed strike!`, 'ability', { cardId: card.id });
        }
    };

    /**
     * Cim — Stamina Thief
     * Standard (onMiss):  when Cim misses, steal opponent's stamina cost (clamp(pAtk*0.12, 8, 20))
     * Variant (onCrit):   65% chance on crit → fully recharge Cim's stamina
     */
    window.abilities.onMiss['B9D29647-29DE-4C1C-82C4-95C04546ECB7'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1]; // Cim — attacker who missed
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }
        const staminaCost = Number(opponent.pAtk) > 0
            ? Math.max(8, Math.min(20, Math.round(Number(opponent.pAtk) * 0.12)))
            : 0;
        if (staminaCost <= 0) { return; }
        if (opponent._throttleStaminaImmune) {
            opponent._throttleStaminaBlockedThisTick = true;
            effects.add(opponent, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(`${opponent.name}'s Overdrive: Stamina Thief blocked!`, 'ability', { cardId: opponent.id });
            }
        } else {
            const stolen = Math.min(staminaCost, Number(opponent.currentStamina) || 0);
            opponent.currentStamina = Math.max(0, (Number(opponent.currentStamina) || 0) - stolen);
            card.currentStamina     = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + stolen);
            effects.add(card, ['powerup']);
            effects.add(opponent, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(`${card.name}'s Stamina Thief stole ${stolen} stamina from ${opponent.name} on a miss!`, 'ability', { cardId: card.id });
            }
        }
    };

    window.abilities.onCrit['B9D29647-29DE-4C1C-82C4-95C04546ECB7'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1]; // Cim — attacker who critted
        if (Math.random() >= 0.65) { return; }
        card.currentStamina = card.maxStamina || 100;
        effects.add(card, ['powerup']);
        if (typeof logFn === 'function') {
            logFn(`${card.name}'s Stamina Thief fully recharged stamina on a critical hit!`, 'ability', { cardId: card.id });
        }
    };

    /**
     * Cipher — Intel
     * Standard (passive): opponent card with highest attack stat loses 25% accuracy per alive Cipher
     * Variant (passive):  that same opponent card cannot land critical hits (critChance = 0)
     * Stacks additively per alive Cipher: 2 Ciphers = −50%, 3 = −75%, etc.
     */
    window.abilities.passive['E40A6248-48C6-4D9E-8903-8643B774866F'] = function(_card, _cardIndex, team, opposingTeam, _opposingIndex, _events, logFn) {
        // Count alive Ciphers on this team
        const cipherCount = team.filter(function(c) {
            return c && c.hp > 0 && c.abilityKey === 'E40A6248-48C6-4D9E-8903-8643B774866F';
        }).length;
        if (cipherCount === 0) { return; }

        // Find opponent card with highest attack stat
        let highestAtk = -1;
        let target = null;
        for (let i = 0; i < opposingTeam.length; i++) {
            const c = opposingTeam[i];
            if (!c || c.hp <= 0) { continue; }
            const atk = Math.max(Number(c.pAtk) || 0, Number(c.mAtk) || 0);
            if (atk > highestAtk) { highestAtk = atk; target = c; }
        }
        if (!target) { return; }

        // Store base accuracy and base crit chance once
        if (target._baseAccuracy === undefined) {
            target._baseAccuracy  = Number(target.accuracy)   || 85;
        }
        if (target._baseCritChance === undefined) {
            target._baseCritChance = Number(target.critChance) || 5;
        }

        // Standard — reduce accuracy by 25% of remaining each Cipher (multiplicative: base × 0.75^count)
        target.accuracy = Math.max(0, Math.round(target._baseAccuracy * Math.pow(0.75, cipherCount)));

        // Variant — no crits on targeted card
        var _origCipherCrit = Number(target.critChance) || 0;
        target.critChance = 0;
        if (_origCipherCrit > 0) { window.flashStatGlow(target, 'critChance', _origCipherCrit, 0); }

        if (typeof logFn === 'function') {
            logFn(`Cipher Intel: ${target.name} accuracy reduced to ${target.accuracy}% and cannot crit (${cipherCount} Cipher(s) active)`, 'ability', { cardId: target.id });
        }
    };

    /**
     * Citrate — Mana Harvest
     * Standard (onMissed): when opponent misses Citrate, gain +10 mana
     */
    window.abilities.onMissed['0713A85A-3ACC-45FC-A2E5-D18ACD700717'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1]; // Citrate — the defender who was missed
        var maxMana = Number(card.maxMana) || 100;
        var before  = Number(card.currentMana) || 0;
        var after   = Math.min(maxMana, before + 10);
        card.currentMana = after;
        effects.add(card, ['glow-blue']);
        card.activeTraits.manaHarvest = { label: 'Mana Harvest +10 (' + after + '/' + maxMana + ')', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + '\'s Mana Harvest: +10 mana from opponent\'s miss! (' + after + '/' + maxMana + ')', 'ability', { cardId: card.id, mana: after });
        }
    };

    /**
     * Claw — Plunder
     * Standard (move):   10% chance to steal and use a random opponent card's ability
     * Variant (onCrit):  10% chance to steal and use a random opponent card's ability on a crit
     *
     * "Use" = copy the stolen ability ID onto Claw and fire all its registered hooks.
     */
    const _plunderAbility = function(team1, team2, index1, index2, events, logFn) {
        const claw = team1[index1];
        if (Math.random() >= 0.30) { return; }

        // Pick a random alive opponent that has an abilityKey
        const alive = team2.filter(function(c) { return c && c.hp > 0 && c.abilityKey; });
        if (alive.length === 0) { return; }
        const chosen = alive[Math.floor(Math.random() * alive.length)];

        // Temporarily copy the stolen ability ID onto Claw, then fire every hook registered for it
        const originalKey        = claw.abilityKey;
        const originalAbility    = claw.ability;
        claw.abilityKey          = chosen.abilityKey;
        claw.ability             = chosen.ability;

        const stolenAbilityName  = chosen.ability || chosen.abilityKey;
        let fired = false;
        try {
            const hooks = ['move','onHit','onCrit','passive','onMiss','onMissed','onKill','damaged','teamCrit'];
            hooks.forEach(function(h) {
                if (!window.abilities[h] || typeof abilities[h][claw.abilityKey] !== 'function') { return; }
                if      (h === 'move')     { abilities.move[claw.abilityKey](team1, team2, index1, index2, events); }
                else if (h === 'onHit')    { abilities.onHit[claw.abilityKey](team1, team2, index1, index2, 0, false, events, logFn); }
                else if (h === 'onCrit')   { abilities.onCrit[claw.abilityKey](team1, team2, index1, index2, events, logFn); }
                else if (h === 'passive')  { abilities.passive[claw.abilityKey](claw, index1, team1, team2, index2, events, logFn); }
                else if (h === 'onMiss')   { abilities.onMiss[claw.abilityKey](team1, team2, index1, index2, events, logFn); }
                else if (h === 'onMissed') { abilities.onMissed[claw.abilityKey](team1, team2, index1, index2, events, logFn); }
                else if (h === 'onKill')   { abilities.onKill[claw.abilityKey](team1, team2, index1, index2, events, logFn); }
                else if (h === 'damaged')  { abilities.damaged[claw.abilityKey](team1, team2, index1, index2, 0, events, false); }
                else if (h === 'teamCrit') { abilities.teamCrit[claw.abilityKey](team1, team2, index1, index2, events); }
                fired = true;
            });
        } catch(err) { /* swallow */ }

        // Restore Claw's original ability ID
        claw.abilityKey = originalKey;
        claw.ability    = originalAbility;

        if (fired) {
            effects.add(claw, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(`${claw.name}'s Plunder stole [${stolenAbilityName}] from ${chosen.name} and used it!`, 'ability', {
                    card: claw.name, ability: 'Plunder', stolen: stolenAbilityName, stolenFrom: chosen.name
                });
            }
        }
    };

    window.abilities.move['679F3F2B-DAA1-44BD-AC9A-BCA17C773A05'] = function(team1, team2, index1, index2, events, logFn) {
        _plunderAbility(team1, team2, index1, index2, events, logFn);
    };

    window.abilities.onCrit['679F3F2B-DAA1-44BD-AC9A-BCA17C773A05'] = function(team1, team2, index1, index2, events, logFn) {
        _plunderAbility(team1, team2, index1, index2, events, logFn);
    };

    /**
     * Chompo — Slow Predator
     * Standard (passive): if opponent is slowed, Chompo's crit chance is tripled
     * Variant (passive):  if Chompo is slowed, negate the slow and gain +10 stamina +10 mana
     */
    window.abilities.passive['D456B25F-87E5-4B2F-B654-AB826563477B'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        // Store base crit chance once so tripling doesn't compound
        if (card._baseCritChance === undefined) {
            card._baseCritChance = Number(card.critChance) || 5;
        }

        // Standard — triple crit chance while opponent is slowed
        const opponent = opposingTeam[opposingIndex];
        const opponentSlowed = !!(opponent && opponent.activeTraits && opponent.activeTraits.slowed && opponent.activeTraits.slowed.turnsLeft > 0);
        card.critChance = opponentSlowed ? card._baseCritChance * 3 : card._baseCritChance;

        // Variant — negate slow on Chompo and gain resources
        const selfSlowed = !!(card.activeTraits && card.activeTraits.slowed && card.activeTraits.slowed.turnsLeft > 0);
        if (selfSlowed) {
            card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + 10);
            card.currentMana    = Math.min(card.maxMana    || 100, (Number(card.currentMana)    || 0) + 10);
            card.activeTraits.slowed.turnsLeft = 0; // negate the slow
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(`${card.name}'s Slow Predator negated slow — gained +10 stamina and +10 mana!`, 'ability', { cardId: card.id });
            }
        }
    };

    // ── Cragjaw — Battle Tempered ─────────────────────────────────────────────
    /**
     * Card: Cragjaw (F2DB73D9-EE4C-4C31-81D1-BD6766DBD065)
     * Ability ID: 0B59D89A-3689-4D29-8BCB-6B3456D941FC
     *
     * Standard: Every critical hit received adds 20 stamina.
     * Variant: Cragjaw cycles through crit resistance every 4 crits received:
     *   1st crit — normal damage
     *   2nd crit — 50% damage (heals back half)
     *   3rd crit — no damage (heals back full damage)
     *   4th crit — heals 1200 HP
     * Cycle repeats from 1st after the 4th.
     * card._critHitsReceived tracks total crits received (resets every 4).
     */
    if (!window.abilities.damaged) {
        window.abilities.damaged = {};
    }
    window.abilities.damaged['0B59D89A-3689-4D29-8BCB-6B3456D941FC'] = function(team1, _team2, index1, _index2, damage, _events, isCrit, logFn) {
        if (!isCrit) { return; }
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        // Track total crits received
        card._critHitsReceived = (card._critHitsReceived || 0) + 1;
        const cycle = ((card._critHitsReceived - 1) % 4) + 1; // 1, 2, 3, 4, 1, 2, 3, 4...

        // Standard: add 20 stamina on every crit
        card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + 20);

        // Update activeTraits badge so the cycle position is visible on the card
        card.activeTraits = card.activeTraits || {};
        card.activeTraits.battleTempered = {
            label: 'Crit ' + card._critHitsReceived + ' (' + cycle + '/4)',
            turnsLeft: 9999  // suppress turn countdown — this is a permanent counter
        };

        // Variant: crit resistance cycle
        let cycleMsg = '';
        if (cycle === 2) {
            // 50% damage — heal back half
            const healBack = Math.round(damage / 2);
            card.hp = card.hp + healBack;
            effects.add(card, ['glow-blue']);
            cycleMsg = `2nd crit — 50% damage absorbed (healed ${healBack})`;
        } else if (cycle === 3) {
            // No damage — heal back full
            card.hp = card.hp + damage;
            effects.add(card, ['glow-green']);
            cycleMsg = `3rd crit — fully negated!`;
        } else if (cycle === 4) {
            // Heal 1200 HP
            card.hp = card.hp + 1200;
            effects.add(card, ['glow-green']);
            cycleMsg = `4th crit — healed 1200 HP!`;
        } else {
            cycleMsg = `1st crit — normal damage`;
        }

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Battle Tempered [crit #${card._critHitsReceived}, cycle ${cycle}/4]: ${cycleMsg} (+20 stamina)`,
                'ability',
                { cardId: card.id, critCount: card._critHitsReceived, cycle }
            );
        }
    };

    // ── Duron — Last Stand ───────────────────────────────────────────────
    /**
     * Card: Duron (B7FD31D1-12B5-4AE2-9121-61C45A04C9F2)
     * Ability ID: 17552322-9819-43A7-82C1-308CAD90FA31
     *
     * onMiss: When Duron is below 15% HP and misses an attack, a random
     * alive teammate's ability fires (all registered hooks for their abilityKey).
     * Uses teammate's own index so self-targeting abilities apply to the teammate.
     * Same pattern as Plunder but targets team instead of opponents.
     */
    window.abilities.onMiss['17552322-9819-43A7-82C1-308CAD90FA31'] = function(team1, team2, index1, index2, events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        // Check below 15% HP using originalHp as the max
        const maxHp = card.originalHp || card.hp;
        const hpPct = (card.hp / maxHp) * 100;
        if (hpPct >= 20) { return; }

        // Find alive teammates with an ability (excluding self)
        const teammates = team1.filter(function(c, i) {
            return i !== index1 && c && c.hp > 0 && c.abilityKey;
        });
        if (teammates.length === 0) { return; }

        const teammate     = teammates[Math.floor(Math.random() * teammates.length)];
        const tmIndex      = team1.indexOf(teammate);
        const tmAbilityKey = teammate.abilityKey;
        const tmAbilityName = teammate.ability || tmAbilityKey;

        let fired = false;
        try {
            const hooks = ['move', 'onHit', 'onCrit', 'passive', 'onMiss', 'onMissed', 'onKill', 'damaged', 'teamCrit'];
            hooks.forEach(function(h) {
                if (!window.abilities[h] || typeof window.abilities[h][tmAbilityKey] !== 'function') { return; }
                if      (h === 'move')     { abilities.move[tmAbilityKey](team1, team2, tmIndex, index2, events, logFn); }
                else if (h === 'onHit')    { abilities.onHit[tmAbilityKey](team1, team2, tmIndex, index2, 0, false, events, logFn); }
                else if (h === 'onCrit')   { abilities.onCrit[tmAbilityKey](team1, team2, tmIndex, index2, events, logFn); }
                else if (h === 'passive')  { abilities.passive[tmAbilityKey](teammate, tmIndex, team1, team2, index2, events, logFn); }
                else if (h === 'onMiss')   { abilities.onMiss[tmAbilityKey](team1, team2, tmIndex, index2, events, logFn); }
                else if (h === 'onMissed') { abilities.onMissed[tmAbilityKey](team1, team2, tmIndex, index2, events, logFn); }
                else if (h === 'onKill')   { abilities.onKill[tmAbilityKey](team1, team2, tmIndex, index2, events, logFn); }
                else if (h === 'damaged')  { abilities.damaged[tmAbilityKey](team1, team2, tmIndex, index2, 0, events, false, logFn); }
                else if (h === 'teamCrit') { abilities.teamCrit[tmAbilityKey](team1, team2, tmIndex, index2, events); }
                fired = true;
            });
        } catch(err) { /* swallow */ }

        if (fired && typeof logFn === 'function') {
            logFn(
                `${card.name}'s Last Stand: HP at ${Math.round(hpPct)}% — triggered ${teammate.name}'s [${tmAbilityName}]!`,
                'ability',
                { cardId: card.id, teammate: teammate.name, ability: tmAbilityName, hpPct: Math.round(hpPct) }
            );
        }
    };

    // ── Durzo — Executioner's Mark ───────────────────────────────────────────
    /**
     * Card: Durzo (2654ED34-059D-484F-BFC0-897E6CB80336)
     * Ability ID: 62C4040E-B0EC-4227-AF6B-92EF133F789E
     *
     * Passive: While the current opponent is below 30% HP, Durzo's accuracy
     * is set to 100 (cannot miss). Restored to _baseAccuracy when above 30%.
     * Uses opponent.originalHp as the max HP baseline.
     */
    window.abilities.passive['62C4040E-B0EC-4227-AF6B-92EF133F789E'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (card._baseAccuracy === undefined) {
            card._baseAccuracy = Number(card.accuracy) || 0;
        }

        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) {
            card.accuracy = card._baseAccuracy;
            return;
        }

        const maxHp   = opponent.originalHp || opponent.hp;
        const hpPct   = (opponent.hp / maxHp) * 100;

        if (hpPct < 30) {
            if (card.accuracy !== 100) {
                card.accuracy = 100;
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Executioner's Mark: ${opponent.name} is at ${Math.round(hpPct)}% HP — attacks cannot miss!`,
                        'ability',
                        { cardId: card.id, opponentHpPct: Math.round(hpPct) }
                    );
                }
            }
            // Persist dark glow and badge every tick while active
            effects.add(card, ['glow-black']);
            card.activeTraits = card.activeTraits || {};
            card.activeTraits.executionerMark = { label: "Executioner's Mark", turnsLeft: 9999 };
        } else {
            card.accuracy = card._baseAccuracy;
            if (card.activeTraits && card.activeTraits.executionerMark) {
                delete card.activeTraits.executionerMark;
            }
        }
    };

    // ── Dreamer — Status Exploit ─────────────────────────────────────────────
    /**
     * Card: Dreamer (F81A66C3-1182-475B-899C-7DDAC6F529A4)
     * Ability ID: 6D3BDEDC-FBD6-43DA-A1C1-E313DD9647D2
     *
     * Passive: While the current opponent has 2 or more active status effects,
     * Dreamer's critChance is set to 100 (all hits guaranteed critical).
     * Restored to _baseCritChance when opponent drops below 2 status effects.
     */
    window.abilities.passive['6D3BDEDC-FBD6-43DA-A1C1-E313DD9647D2'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (card._baseCritChance === undefined) {
            card._baseCritChance = Number(card.critChance) || 0;
        }

        const opponent = opposingTeam[opposingIndex];

        const opponentStatusCount = (opponent && opponent.activeTraits)
            ? Object.keys(opponent.activeTraits).filter(function(t) {
                return opponent.activeTraits[t] && opponent.activeTraits[t].turnsLeft > 0;
              }).length
            : 0;

        const selfStatusCount = card.activeTraits
            ? Object.keys(card.activeTraits).filter(function(t) {
                return card.activeTraits[t] && card.activeTraits[t].turnsLeft > 0;
              }).length
            : 0;

        if (opponentStatusCount >= 2 || selfStatusCount >= 2) {
            if (card.critChance !== 100) {
                card.critChance = 100;
                const reason = selfStatusCount >= 2 && opponentStatusCount >= 2
                    ? `Dreamer has ${selfStatusCount} and ${opponent ? opponent.name : 'opponent'} has ${opponentStatusCount} status effects`
                    : selfStatusCount >= 2
                        ? `Dreamer has ${selfStatusCount} status effects`
                        : `${opponent ? opponent.name : 'opponent'} has ${opponentStatusCount} status effects`;
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Status Exploit: ${reason} — all hits are critical!`,
                        'ability',
                        { cardId: card.id, selfStatusCount, opponentStatusCount }
                    );
                }
            }
        } else {
            card.critChance = card._baseCritChance;
        }
    };

    // ── Drax — Stamina Drain ─────────────────────────────────────────────────
    /**
     * Card: Drax (B9D872C7-7C33-4C95-853F-3B562FB81C59)
     * Ability ID: 6F94132F-7FB0-4347-A906-EDFEFA5BEE40
     *
     * Passive: The current opponent's critChance is reduced by 1000 for every
     * 10 points of stamina they have lost.
     *   staminaLost = maxStamina - currentStamina
     *   reduction   = floor(staminaLost / 10) * 1000
     *   opponent.critChance = max(0, _baseCritChance - reduction)
     *
     * Recalculated every passive tick so it stays in sync with stamina changes.
     * opponent._baseCritChance stored once to prevent compounding.
     */
    window.abilities.passive['6F94132F-7FB0-4347-A906-EDFEFA5BEE40'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        // Store base crit chance once
        if (opponent._baseCritChance === undefined) {
            opponent._baseCritChance = Number(opponent.critChance) || 0;
        }

        const maxStamina    = opponent.maxStamina || 100;
        const staminaLost   = Math.max(0, maxStamina - (Number(opponent.currentStamina) || 0));
        const reduction     = Math.floor(staminaLost / 10) * 1000;
        const newCritChance = Math.max(0, opponent._baseCritChance - reduction);

        if (opponent.critChance !== newCritChance) {
            var _origDraxCrit = opponent.critChance;
            opponent.critChance = newCritChance;
            window.flashStatGlow(opponent, 'critChance', _origDraxCrit, newCritChance);
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Stamina Drain: ${opponent.name} lost ${staminaLost} stamina — crit chance reduced to ${newCritChance} (-${reduction})`,
                    'ability',
                    { cardId: card.id, staminaLost, reduction, critChance: newCritChance }
                );
            }
        }
    };

    // ── Dr Midnight — Confusion Reversal ─────────────────────────────────────
    /**
     * Card: Dr Midnight (27AB762F-FE66-4471-AF27-58AB8E9F140A)
     * Ability ID: 6734BB92-D0D6-401E-95E0-278A4059215C
     *
     * Passive: When Dr Midnight has the confused status, there is a 60% chance
     * it gets reversed — removed from Dr Midnight and applied to the current opponent.
     * The check fires once per confused application via _confusionReversed flag.
     * If the check fails (40%), the confused remains for its normal duration.
     */
    window.abilities.passive['6734BB92-D0D6-401E-95E0-278A4059215C'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (!window.traits) { return; }

        if (!traits.has(card, 'confused')) {
            // Confused is gone — clear reversal flag so next application can be checked
            delete card._confusionReversed;
            return;
        }

        // Confused is active — attempt reversal once per application
        if (card._confusionReversed) { return; }
        card._confusionReversed = true;

        if (Math.random() < 0.90) {
            const opponent = opposingTeam[opposingIndex];
            traits.remove(card, 'confused');
            if (opponent && opponent.hp > 0) {
                traits.apply(opponent, 'confused');
            }
            effects.add(card, ['glow-blue']);
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Confusion Reversal: confused status reflected back onto ${opponent ? opponent.name : 'opponent'}!`,
                    'ability',
                    { cardId: card.id }
                );
            }
        } else {
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Confusion Reversal failed (40%) — confused status remains.`,
                    'ability',
                    { cardId: card.id }
                );
            }
        }
    };

    // ── Dexter Morgan — Blood Fury ───────────────────────────────────────────
    /**
     * Card: Dexter Morgan (066DBBCC-1EB3-40BA-A177-DFCF81EB5942)
     * Ability ID: 62FE15DB-0BFF-4BE5-88CD-938415EBA991
     *
     * onMiss: Track consecutive misses. On the 3rd in a row:
     *   - Double pAtk and mAtk (100% damage boost)
     *   - Set _damageBonusTurns = 3
     *   - Reset consecutive miss counter
     * onHit: Reset consecutive miss counter.
     * Both onMiss and onHit tick _damageBonusTurns down by 1 AFTER the attack
     * so exactly 3 attacks benefit from the boost before it expires.
     *
     * card._consecutiveMisses — running miss streak
     * card._damageBonusTurns  — turns remaining on the buff
     * card._basePAtk / _baseMAtk — original stats for restore on expiry
     */
    var _bloodFuryTick = function(card, logFn) {
        if (!card._damageBonusTurns || card._damageBonusTurns <= 0) { return; }
        card._damageBonusTurns -= 1;
        if (card.activeTraits && card.activeTraits.bloodFury) {
            card.activeTraits.bloodFury.turnsLeft = card._damageBonusTurns;
        }
        if (card._damageBonusTurns <= 0) {
            if (card._basePAtk !== undefined) { card.pAtk = card._basePAtk; }
            if (card._baseMAtk !== undefined) { card.mAtk = card._baseMAtk; }
            delete card._damageBonusTurns;
            if (card.activeTraits) { delete card.activeTraits.bloodFury; }
            effects.add(card, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(`${card.name}'s Blood Fury faded — damage restored.`, 'ability', { cardId: card.id });
            }
        }
    };
    window.abilities.onMiss['62FE15DB-0BFF-4BE5-88CD-938415EBA991'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        // Tick buff first (attack just resolved as a miss)
        _bloodFuryTick(card, logFn);

        // Track consecutive misses
        card._consecutiveMisses = (card._consecutiveMisses || 0) + 1;

        if (card._consecutiveMisses >= 3) {
            // Store base stats once so restore is always accurate
            if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
            if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }
            card.pAtk = card._basePAtk * 2;
            card.mAtk = card._baseMAtk * 2;
            card._damageBonusTurns = 3;
            card._consecutiveMisses = 0;
            card.activeTraits = card.activeTraits || {};
            card.activeTraits.bloodFury = { label: 'Blood Fury', turnsLeft: 3 };
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Blood Fury activated: 3 consecutive misses — 100% damage boost for 3 turns! (pAtk: ${card.pAtk}, mAtk: ${card.mAtk})`,
                    'ability',
                    { cardId: card.id, pAtk: card.pAtk, mAtk: card.mAtk }
                );
            }
        } else {
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Blood Fury: ${card._consecutiveMisses}/3 consecutive misses`,
                    'ability',
                    { cardId: card.id, consecutiveMisses: card._consecutiveMisses }
                );
            }
        }
    };
    window.abilities.onHit['62FE15DB-0BFF-4BE5-88CD-938415EBA991'] = function(team1, _team2, index1, _index2, _damage, _critical, _events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        // A successful hit breaks the miss streak
        card._consecutiveMisses = 0;

        // Tick buff (attack just resolved as a hit)
        _bloodFuryTick(card, logFn);
    };

    // ── Emperor Palpatine — Shock Absorption ─────────────────────────────────
    /**
     * Card: Emporer Palpatine (6ACD5DF5-349A-4F11-B9A1-7F1143693101)
     * Ability ID: 88C6B42C-F924-4445-AF8D-B9D8ED6672F5
     *
     * Passive:
     *   1. Sets card._shockNullsAbility = false every tick so traits.js will NOT
     *      null Palpatine's abilityKey when shocked is applied (allows this passive
     *      to continue firing while shocked is active).
     *   2. While shocked: negate accuracy/speed debuffs by restoring saved values
     *      from the trait entry, and grant +1500 pAtk & mAtk (once per shock).
     *   3. When shocked expires: restore base pAtk/mAtk.
     */
    window.abilities.passive['88C6B42C-F924-4445-AF8D-B9D8ED6672F5'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        // Always flag so shocked cannot disable this ability
        card._shockNullsAbility = false;

        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        const isShocked = !!(window.traits && traits.has(card, 'shocked'));

        if (isShocked) {
            // Negate shocked accuracy & speed debuffs using saved values in trait entry
            const entry = card.activeTraits && card.activeTraits.shocked;
            if (entry) {
                if (entry.savedAccuracy !== undefined) { card.accuracy = entry.savedAccuracy; }
                if (entry.savedSpeed    !== undefined) { card.speed    = entry.savedSpeed; }
            }
            // Apply +1500 attack bonus once per shock application
            if (!card._shockBonusActive) {
                var _origPalpatinePAtk = card.pAtk;
                var _origPalpatineMAtk = card.mAtk;
                card.pAtk = card._basePAtk + 1500;
                card.mAtk = card._baseMAtk + 1500;
                card._shockBonusActive = true;
                window.flashStatGlow(card, 'pAtk', _origPalpatinePAtk, card.pAtk);
                window.flashStatGlow(card, 'mAtk', _origPalpatineMAtk, card.mAtk);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Shock Absorption: shocked is ineffective — gained +1500 pAtk & mAtk!`,
                        'ability',
                        { cardId: card.id, pAtk: card.pAtk, mAtk: card.mAtk }
                    );
                }
            }
        } else {
            // Shocked expired — restore base attack stats
            if (card._shockBonusActive) {
                card.pAtk = card._basePAtk;
                card.mAtk = card._baseMAtk;
                card._shockBonusActive = false;
            }
        }
    };

    // ── Goku — Full Power ────────────────────────────────────────────────────
    /**
     * Card: Goku (4E45516E-5D5F-4355-B816-E8E723AA7938)
     * Ability ID: 21A8DC4A-3A55-485D-8953-C7FFA76878FD
     *
     * onCrit: If currentStamina >= 100 when a critical hit lands, apply an
     * additional 50% of physical damage (pAtk - opponent armor) to the defender.
     * onCrit fires AFTER damageTaken, so this is a follow-up hit.
     */
    window.abilities.onCrit['21A8DC4A-3A55-485D-8953-C7FFA76878FD'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1];
        const opponent = team2[index2];
        if (!card || !opponent || opponent.hp <= 0) { return; }

        // Condition: stamina must be 100 or above
        if ((Number(card.currentStamina) || 0) < 100) { return; }

        const armor      = Number(opponent.armor) || Number(opponent.defence) || 0;
        const physDmg    = Math.max(0, (Number(card.pAtk) || 0) - armor);
        const bonusDmg   = Math.round(physDmg * 0.50);
        if (bonusDmg <= 0) { return; }

        opponent.hp = Math.max(0, opponent.hp - bonusDmg);

        effects.add(card, ['glow-gold']);
        effects.add(opponent, ['shakeY']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Full Power: stamina full — crit bonus +${bonusDmg} physical damage! (${opponent.name} HP: ${opponent.hp})`,
                'ability',
                { cardId: card.id, bonusDmg, opponentHp: opponent.hp }
            );
        }
    };

    // ── Frieza — Crisis Speed ────────────────────────────────────────────────
    /**
     * Card: Frieza (11D49946-763A-4186-81DD-42F7332ED288)
     * Ability ID: 4B6B5AE3-A593-491E-9876-5342695F8054
     *
     * Passive: When the current opponent's critChance exceeds Frieza's,
     * speed is set to _baseSpeed * 1.25 (+25%).
     * Restored to _baseSpeed when the condition is no longer met.
     */
    window.abilities.passive['4B6B5AE3-A593-491E-9876-5342695F8054'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (card._baseSpeed === undefined) {
            card._baseSpeed = Number(card.speed) || 0;
        }

        const opponent = opposingTeam[opposingIndex];
        const opponentCrit = opponent ? (Number(opponent.critChance) || 0) : 0;
        const selfCrit     = Number(card.critChance) || 0;

        if (opponentCrit > selfCrit) {
            const boostedSpeed = Math.round(card._baseSpeed * 1.25);
            if (card.speed !== boostedSpeed) {
                card.speed = boostedSpeed;
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Crisis Speed: opponent crit (${opponentCrit}%) exceeds own (${selfCrit}%) — speed boosted to ${card.speed}!`,
                        'ability',
                        { cardId: card.id, speed: card.speed, opponentCrit, selfCrit }
                    );
                }
            }
        } else {
            if (card.speed !== card._baseSpeed) {
                card.speed = card._baseSpeed;
            }
        }
    };

    // ── Freight — Evasion Charge ─────────────────────────────────────────────
    /**
     * Card: Freight (BCC7D817-3F1A-4C45-A778-2F0397382AEB)
     * Ability ID: 6DB47F64-394E-4F2A-A041-67D9C4BD706E
     *
     * onMissed: Every time an opponent's attack misses (Freight dodges),
     * gain +10 stamina (capped at maxStamina).
     */
    window.abilities.onMissed['6DB47F64-394E-4F2A-A041-67D9C4BD706E'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + 10);

        effects.add(card, ['powerup']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Evasion Charge: dodged an attack — gained +10 stamina! (${card.currentStamina}/${card.maxStamina || 100})`,
                'ability',
                { cardId: card.id, stamina: card.currentStamina }
            );
        }
    };

    // ── Finalis — Retribution ────────────────────────────────────────────────
    /**
     * Card: Finalis (51C48699-F223-4712-B6B8-FCDF1DB7857D)
     * Ability ID: 9A71D0F9-8481-402E-94B2-E933221D3B23
     *
     * damaged: When the opponent lands a critical hit on Finalis, 20% chance to
     * cause lockdown — the attacker's next turn is skipped (turnSkips + 1).
     * An activeTraits badge 'lockdown' shows on the attacker while locked down.
     */
    window.abilities.damaged['9A71D0F9-8481-402E-94B2-E933221D3B23'] = function(team1, team2, index1, index2, _damage, _events, isCrit, logFn) {
        if (!isCrit) { return; }

        const card     = team1[index1]; // Finalis (the one hit)
        const attacker = team2[index2]; // the one who critted
        if (!attacker || attacker.hp <= 0) { return; }

        if (Math.random() >= 0.90) { return; }

        attacker.turnSkips = (attacker.turnSkips || 0) + 1;
        attacker.activeTraits = attacker.activeTraits || {};
        attacker.activeTraits.lockdown = { label: 'Lockdown', turnsLeft: attacker.turnSkips };

        effects.add(attacker, ['shakeY']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Retribution: ${attacker.name}'s critical hit triggered lockdown — next turn skipped!`,
                'ability',
                { cardId: card.id, attacker: attacker.name }
            );
        }
    };

    // ── Falcone — Last Chance ────────────────────────────────────────────────
    /**
     * Card: Falcone (1DC6F951-2CD1-482A-8095-B46251114ED9)
     * Ability ID: 945FA0E6-9C71-45DB-B333-C4F3203173CF
     *
     * Passive (standard): When Falcone is below 15% HP, reduce the current
     * opponent's accuracy to 20 (giving Falcone an 80% dodge chance).
     * Restored when Falcone recovers above 15% HP or the opponent changes.
     *
     * onMissed (variant): When Falcone is below 15% HP and the opponent misses,
     * Falcone automatically counter-attacks for (pAtk - opponent armor) damage.
     */
    window.abilities.passive['945FA0E6-9C71-45DB-B333-C4F3203173CF'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        const maxHp = card.originalHp || card.hp;
        const hpPct = (card.hp / maxHp) * 100;

        if (hpPct < 15) {
            // Store base accuracy once per opponent
            if (opponent._falconeBaseAccuracy === undefined) {
                opponent._falconeBaseAccuracy = Number(opponent.accuracy) || 0;
            }
            if (opponent.accuracy !== 20) {
                opponent.accuracy = 20;
                effects.add(card, ['glow-green']);
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Last Chance: HP critical (${Math.round(hpPct)}%) — dodge chance 80%! (${opponent.name} accuracy → 20)`,
                        'ability',
                        { cardId: card.id, hpPct: Math.round(hpPct) }
                    );
                }
            }
        } else {
            // Restore opponent accuracy when Falcone is no longer critical
            if (opponent._falconeBaseAccuracy !== undefined) {
                opponent.accuracy = opponent._falconeBaseAccuracy;
                delete opponent._falconeBaseAccuracy;
            }
        }
    };
    window.abilities.onMissed['945FA0E6-9C71-45DB-B333-C4F3203173CF'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1]; // Falcone (the one who was missed)
        const attacker = team2[index2]; // the one who missed

        const maxHp = card.originalHp || card.hp;
        const hpPct = (card.hp / maxHp) * 100;
        if (hpPct >= 15) { return; }

        // Counter-attack: deal pAtk minus attacker's armor
        const armor       = Number(attacker.armor) || Number(attacker.defence) || 0;
        const counterDmg  = Math.max(1, (Number(card.pAtk) || 0) - armor);
        attacker.hp       = Math.max(0, attacker.hp - counterDmg);

        effects.add(card, ['powerup']);
        effects.add(attacker, ['shakeY']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Last Chance counter-attack: ${attacker.name} missed and took ${counterDmg} damage!`,
                'ability',
                { cardId: card.id, counterDmg, attackerHp: attacker.hp }
            );
        }
    };

    // ── Falcon — Regen Theft ─────────────────────────────────────────────────
    /**
     * Card: Falcon (C0B0F8C5-A439-41C1-B04A-54C90DEEFDB8)
     * Ability ID: 5C227D9C-C6CB-4218-9E3F-13A2E6FDFBA8
     *
     * Passive: Before each attack, 25% chance to steal the opponent's stamina
     * regeneration for this attack cycle:
     *   - Sets opponent._staminaRegenMult = 0 so regenerateResource() gives them 0
     *   - Credits Falcon with the amount the opponent would have regenerated
     *   - Resets opponent._staminaRegenMult = 1 on the following passive tick
     *
     * opponent._falconRegenStolen flag prevents double-firing within the same
     * attack cycle and ensures the mult is restored exactly once.
     */
    window.abilities.passive['5C227D9C-C6CB-4218-9E3F-13A2E6FDFBA8'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        // Restore opponent's regen mult after the previous steal was applied
        if (opponent._falconRegenStolen) {
            opponent._staminaRegenMult = 1;
            delete opponent._falconRegenStolen;
            return; // one action per tick — don't re-roll in the same passive call
        }

        // 25% chance to steal this attack cycle's regen
        if (Math.random() < 0.25) {
            const regenAmt = Math.max(1, Math.round((opponent.maxStamina || 100) * 0.06));

            // Suppress opponent's regen for this cycle
            opponent._staminaRegenMult = 0;
            opponent._falconRegenStolen = true;

            // Credit Falcon with the stolen regen
            card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + regenAmt);

            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Regen Theft: stole ${regenAmt} stamina regen from ${opponent.name}!`,
                    'ability',
                    { cardId: card.id, stolen: regenAmt, opponentStamina: opponent.currentStamina }
                );
            }
        }
    };

    // ── Eon — Amplified ──────────────────────────────────────────────────────
    /**
     * Card: Eon (9C8AD8A7-F945-4B9E-BF38-356C4B39CE71)
     * Ability ID: FE5A0A64-4611-4499-88C5-750B3FCF0B7F
     *
     * Passive: Any pAtk/mAtk above base (i.e. a buff) is 10% more effective.
     *
     * Anti-compounding: each tick, the previous bonus (_eonBonusPAtk/mAtk) is
     * subtracted first to get the raw external value, then a fresh 10% bonus is
     * applied on top. This ensures the 10% is always calculated against the raw
     * buff, not a previously-enhanced value.
     *
     *   rawBuff = (card.pAtk - _eonBonusPAtk) - _basePAtk
     *   card.pAtk = (card.pAtk - _eonBonusPAtk) + floor(rawBuff * 0.10)
     */
    window.abilities.passive['FE5A0A64-4611-4499-88C5-750B3FCF0B7F'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        // ── pAtk ──
        const prevBonusP = card._eonBonusPAtk || 0;
        const rawPAtk    = Number(card.pAtk) - prevBonusP;
        const rawBuffP   = Math.max(0, rawPAtk - card._basePAtk);
        const newBonusP  = Math.round(rawBuffP * 0.10);
        var _origEonPAtk = Number(card.pAtk);
        card.pAtk          = rawPAtk + newBonusP;
        card._eonBonusPAtk = newBonusP;
        if (newBonusP > 0 && card.pAtk > _origEonPAtk) { window.flashStatGlow(card, 'pAtk', _origEonPAtk, card.pAtk); }

        // ── mAtk ──
        const prevBonusM = card._eonBonusMAtk || 0;
        const rawMAtk    = Number(card.mAtk) - prevBonusM;
        const rawBuffM   = Math.max(0, rawMAtk - card._baseMAtk);
        const newBonusM  = Math.round(rawBuffM * 0.10);
        var _origEonMAtk = Number(card.mAtk);
        card.mAtk          = rawMAtk + newBonusM;
        card._eonBonusMAtk = newBonusM;
        if (newBonusM > 0 && card.mAtk > _origEonMAtk) { window.flashStatGlow(card, 'mAtk', _origEonMAtk, card.mAtk); }

        if ((newBonusP > 0 || newBonusM > 0) && typeof logFn === 'function') {
            if (newBonusP !== prevBonusP || newBonusM !== prevBonusM) {
                logFn(
                    `${card.name}'s Amplified: damage buffs enhanced (+${newBonusP} pAtk, +${newBonusM} mAtk)`,
                    'ability',
                    { cardId: card.id, bonusPAtk: newBonusP, bonusMAtk: newBonusM }
                );
            }
        }
    };

    // ── Elvis — Abduction ────────────────────────────────────────────────────
    /**
     * Card: Elvis (79F7CC47-69CB-4D1D-8C80-4BB09C08C035)
     * Ability ID: 7EDF18A0-999B-402C-B6C6-AE08E04CBE31
     *
     * move: 25% chance to drain 30% of the opponent's current stamina each attack.
     */
    window.abilities.move['7EDF18A0-999B-402C-B6C6-AE08E04CBE31'] = function(team1, team2, index1, index2, _events, logFn) {
        if (Math.random() >= 0.9) { return; }

        const card     = team1[index1];
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }

        if (opponent._throttleStaminaImmune) {
            opponent._throttleStaminaBlockedThisTick = true;
            effects.add(opponent, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(`${opponent.name}'s Overdrive: Abduction stamina drain blocked!`, 'ability', { cardId: opponent.id });
            }
            return;
        }

        const staminaDrained = Math.round((Number(opponent.currentStamina) || 0) * 0.30);
        if (staminaDrained <= 0) { return; }

        opponent.currentStamina = Math.max(0, (Number(opponent.currentStamina) || 0) - staminaDrained);
        effects.add(opponent, ['shakeY']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Abduction: drained ${staminaDrained} stamina from ${opponent.name}! (${opponent.currentStamina} remaining)`,
                'ability',
                { cardId: card.id, staminaDrained, opponentStamina: opponent.currentStamina }
            );
        }
    };

    // ── DareDevil — Rising Odds ──────────────────────────────────────────────
    /**
     * Card: DareDevil (E3ED95E6-B903-425C-8239-578F01B3E9EB)
     * Ability ID: 9B737978-22E4-4962-A5CD-66A4F8DBFEA5
     *
     * onMiss: Every missed attack adds +15 critChance (stacks indefinitely).
     * card._missAccBonus tracks total bonus accumulated.
     */
    window.abilities.onMiss['9B737978-22E4-4962-A5CD-66A4F8DBFEA5'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        card._missAccBonus = (card._missAccBonus || 0) + 15;
        card.critChance = (Number(card.critChance) || 0) + 15;

        effects.add(card, ['powerup']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Rising Odds: +15 crit chance on miss! (now ${card.critChance}%, total bonus +${card._missAccBonus}%)`,
                'ability',
                { cardId: card.id, critChance: card.critChance, totalBonus: card._missAccBonus }
            );
        }
    };

    // ── Cyclops — Dead Eye ───────────────────────────────────────────────────
    /**
     * Card: Cyclops (8B72256A-CEE0-4D43-90D8-B0A5B2F462CE)
     * Ability ID: DCCBDE52-9C09-4A23-90FA-DEDF26612343
     *
     * onCrit: Every critical hit adds +30 accuracy (stacks, no cap — exceeding
     * 100 guarantees hits since the hit check is random * 100 < accuracy).
     * card._critAccBonus tracks total bonus accumulated.
     */
    window.abilities.onCrit['DCCBDE52-9C09-4A23-90FA-DEDF26612343'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1];
        if (!card || card.hp <= 0) { return; }

        card._critAccBonus = (card._critAccBonus || 0) + 30;
        card.accuracy = (Number(card.accuracy) || 0) + 30;

        effects.add(card, ['powerup']);
        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Dead Eye: +30 accuracy on crit! (now ${card.accuracy}, total bonus +${card._critAccBonus})`,
                'ability',
                { cardId: card.id, accuracy: card.accuracy, totalBonus: card._critAccBonus }
            );
        }
    };

    // ── Kami — Final Gift ─────────────────────────────────────────────────────
    /**
     * Card: Kami (F8622164-B0DA-4F13-859C-67942BB72443)
     * Ability ID: FB7C4447-7870-4C4E-8D45-07920A8855F1
     *
     * onDefeated: When Kami is killed, calculate excess damage (overkill).
     *   Per 1000 excess damage:
     *     +10 stamina  → passed to the next living teammate
     *     +400 pAtk   → passed to the next living teammate
     *   Next card also receives a glow-green effect and a badge.
     *
     * Requires the onDefeated hook dispatcher in battleArena.js.
     */
    window.abilities.onDefeated['FB7C4447-7870-4C4E-8D45-07920A8855F1'] = function(team1, _team2, index1, _index2, excessDamage, _events, logFn) {
        const nextCard = team1.find(function(c, i) { return i !== index1 && c && c.hp > 0; });
        if (!nextCard) { return; }

        const stacks       = Math.floor(excessDamage / 1000);
        const staminaBonus = stacks * 10;
        const pAtkBonus    = stacks * 400;

        if (staminaBonus > 0) {
            nextCard.currentStamina = Math.min(nextCard.maxStamina || 100, (Number(nextCard.currentStamina) || 0) + staminaBonus);
        }
        if (pAtkBonus > 0) {
            nextCard.pAtk = (Number(nextCard.pAtk) || 0) + pAtkBonus;
        }

        // Visual indicator on the next card
        effects.add(nextCard, ['glow-green']);
        nextCard.activeTraits = nextCard.activeTraits || {};
        nextCard.activeTraits.kamiLegacy = {
            label: `Kami's Gift${staminaBonus > 0 ? ' +' + staminaBonus + ' sta' : ''}${pAtkBonus > 0 ? ' +' + pAtkBonus + ' pAtk' : ''}`,
            turnsLeft: 9999
        };

        if (typeof logFn === 'function') {
            logFn(
                `Kami's Final Gift: ${excessDamage} excess damage → ${nextCard.name} receives +${staminaBonus} stamina and +${pAtkBonus} pAtk`,
                'ability',
                { cardId: team1[index1].id, excessDamage, staminaBonus, pAtkBonus, nextCard: nextCard.name }
            );
        }
    };

    // ── Kane — Chaos Contagion ────────────────────────────────────────────────
    /**
     * Card: Kane (3088F1DF-D461-41A8-9ADF-F7AA507DCAB6)
     * Ability ID: 3FA1686F-8F85-4211-9FB4-2D962B8A3EA7
     *
     * Passive: 70% chance each turn — if the opponent has any active debuffs,
     * pick one at random and apply it to a random living teammate (not Kane).
     * Debuffs spread via traits.apply(), so immunity and refresh rules apply.
     */
    var _kaneDebuffList = [
        'slowed', 'staggered', 'pacified', 'shocked', 'withered', 'broken',
        'dominated', 'disarmed', 'hallucinating', 'bleeding', 'frozen',
        'burning', 'poisoned', 'stunned', 'weakened', 'blinded'
    ];
    window.abilities.passive['3FA1686F-8F85-4211-9FB4-2D962B8A3EA7'] = function(card, cardIndex, team, opposingTeam, opposingIndex, _events, logFn) {
        if (Math.random() >= 0.70) { return; }

        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0 || !opponent.activeTraits) { return; }
        if (!window.traits || typeof traits.apply !== 'function') { return; }

        // Collect debuffs the opponent currently has active
        var activeDebuffs = _kaneDebuffList.filter(function(t) {
            return opponent.activeTraits[t] && opponent.activeTraits[t].turnsLeft > 0;
        });
        if (activeDebuffs.length === 0) { return; }

        // Pick a random debuff from the opponent's active list
        var debuff = activeDebuffs[Math.floor(Math.random() * activeDebuffs.length)];

        // Pick a random living teammate (not Kane)
        var teammates = team.filter(function(c, i) { return i !== cardIndex && c && c.hp > 0; });
        if (teammates.length === 0) { return; }
        var target = teammates[Math.floor(Math.random() * teammates.length)];

        traits.apply(target, debuff);
        effects.add(target, ['glow-red']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Chaos Contagion: ${opponent.name}'s ${debuff} spreads to ${target.name}!`,
                'ability',
                { cardId: card.id, debuff, targetId: target.id }
            );
        }
    };

    // ── Katono — Relentless Pursuit ───────────────────────────────────────────
    /**
     * Card: Katono (C44FCE95-E1BC-4D4E-8E91-26F30818FDD8)
     * Ability ID: 91399390-9CAA-4C32-BFFC-09FE743422D0
     *
     * move: Record the stamina/mana cost before battleArena deducts it,
     *       so onMiss can refund the exact amount.
     *
     * onMiss:
     *   1. +10 critChance (stacks every dodge).
     *   2. Refund the stamina/mana cost that was deducted for this missed attack.
     *
     * Cost formula mirrors battleArena.js:
     *   staminaCost = pAtk > 0 ? max(8, min(20, round(pAtk × 0.12))) : 0
     *   manaCost    = mAtk > 0 ? max(8, min(20, round(mAtk × 0.12))) : 0
     */
    window.abilities.move['91399390-9CAA-4C32-BFFC-09FE743422D0'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        const card = team1[index1];
        const pAtk = Number(card.pAtk) || 0;
        const mAtk = Number(card.mAtk) || 0;
        // Snapshot cost before deduction so onMiss can refund accurately
        card._katono_staminaCost = pAtk > 0 ? Math.max(8, Math.min(20, Math.round(pAtk * 0.12))) : 0;
        card._katono_manaCost    = mAtk > 0 ? Math.max(8, Math.min(20, Math.round(mAtk * 0.12))) : 0;
    };

    window.abilities.onMiss['91399390-9CAA-4C32-BFFC-09FE743422D0'] = function(team1, _team2, index1, _index2, _events, logFn) {
        const card = team1[index1];

        // +10 crit chance per dodge (stacks)
        card._baseCritChance = card._baseCritChance !== undefined ? card._baseCritChance : (Number(card.critChance) || 0);
        var _origKatonoCrit = Number(card.critChance) || 0;
        card.critChance = _origKatonoCrit + 10;
        window.flashStatGlow(card, 'critChance', _origKatonoCrit, card.critChance);

        // Refund stamina and mana for this missed attack
        if (card._katono_staminaCost > 0) {
            card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + card._katono_staminaCost);
        }
        if (card._katono_manaCost > 0) {
            card.currentMana = Math.min(card.maxMana || 100, (Number(card.currentMana) || 0) + card._katono_manaCost);
        }

        card.activeTraits = card.activeTraits || {};
        card.activeTraits.relentless = { label: `Relentless +${card.critChance - card._baseCritChance}% crit`, turnsLeft: 9999 };

        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Relentless Pursuit: +10 critChance (now ${card.critChance}), stamina/mana refunded`,
                'ability',
                { cardId: card.id, critChance: card.critChance }
            );
        }
    };

    // ── Kalyx — Mirror Strike ────────────────────────────────────────────────
    /**
     * Card: Kalyx (757C598F-9B85-4052-83B5-17719EF12E1C)
     * Ability ID: 02E7F113-078F-41B9-8AC4-94ADCD65C50C
     *
     * move (10% chance): Borrow the opponent's pAtk for this attack.
     *   Saves Kalyx's own pAtk, replaces it with opponent's pAtk.
     * onHit / onMiss: Restore Kalyx's original pAtk after the attack resolves.
     */
    window.abilities.move['02E7F113-078F-41B9-8AC4-94ADCD65C50C'] = function(team1, team2, index1, index2, _events, logFn) {
        if (Math.random() >= 0.10) { return; }
        const card     = team1[index1];
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }

        card._kalyxStolenPAtk = Number(card.pAtk) || 0;
        card.pAtk             = card._kalyxStolenPAtk + (Number(opponent.pAtk) || 0);
        card._kalyxActive     = true;

        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Mirror Strike: adding ${opponent.name}'s pAtk — pAtk boosted to ${card.pAtk} this attack!`,
                'ability',
                { cardId: card.id, boostedPAtk: card.pAtk }
            );
        }
    };

    // Restore pAtk after a hit
    window.abilities.onHit['02E7F113-078F-41B9-8AC4-94ADCD65C50C'] = function(team1, _team2, index1, _index2, _damage, _critical, _events, _logFn) {
        const card = team1[index1];
        if (!card._kalyxActive) { return; }
        card.pAtk         = card._kalyxStolenPAtk;
        card._kalyxActive = false;
        delete card._kalyxStolenPAtk;
    };

    // Restore pAtk after a miss
    window.abilities.onMiss['02E7F113-078F-41B9-8AC4-94ADCD65C50C'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        const card = team1[index1];
        if (!card._kalyxActive) { return; }
        card.pAtk         = card._kalyxStolenPAtk;
        card._kalyxActive = false;
        delete card._kalyxStolenPAtk;
    };

    // ── Joker — Wild Card ────────────────────────────────────────────────────
    /**
     * Card: Joker (C9C511EA-A0ED-4BFE-A1D7-2DA66285A1AF)
     * Ability ID: 72888296-EBE5-4AC6-B649-BADE6C783C86
     *
     * move — two independent rolls each turn:
     *
     * Wild Swap (70%):
     *   Swap the first and last living cards in the opponent's team.
     *
     * Chaos Shuffle (50%):
     *   Randomly rearrange all living cards in the opponent's team.
     *
     * Dead cards (hp <= 0) are ignored — only living positions are affected.
     * After any swap or shuffle, the card now at index 1 gets a flip animation.
     */
    window.abilities.move['72888296-EBE5-4AC6-B649-BADE6C783C86'] = function(team1, team2, index1, _index2, _events, logFn) {
        const card = team1[index1];

        // Flip the card now at team2[0].
        // Uses splice() for the swap/shuffle so Vue 2 detects the mutation and
        // re-renders. The flip is deferred via setTimeout(0) to run after that
        // render, so jQuery finds the card at its new visual position.
        function flipNextCard() {
            var next = team2[0];
            if (!next || !next.id) { return; }
            // Defer so Vue re-renders the swapped array before we query the DOM.
            // No removeClass needed — cardFlip ends at rotateY(0deg) (front face)
            // and Vue removes the class on its next render (model.show = false).
            setTimeout(function() {
                $('#' + next.id).find('.playing-card').addClass('flipped');
            }, 0);
        }

        // Collect indices of living cards in opponent team
        var livingIndices = [];
        for (var i = 0; i < team2.length; i++) {
            if (team2[i] && team2[i].hp > 0) { livingIndices.push(i); }
        }
        if (livingIndices.length < 2) { return; } // nothing to rearrange

        // ── Wild Swap (70%) — first & last living cards switch positions ──
        // splice() is used instead of direct index assignment so Vue 2 reacts.
        if (Math.random() < 0.70) {
            var firstIdx  = livingIndices[0];
            var lastIdx   = livingIndices[livingIndices.length - 1];
            var firstCard = team2[firstIdx];
            var lastCard  = team2[lastIdx];
            // Replace lastIdx first (higher index) so firstIdx stays valid
            team2.splice(lastIdx,  1, firstCard);
            team2.splice(firstIdx, 1, lastCard);

            flipNextCard();
            effects.add(card, ['powerup']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Wild Swap: ${lastCard.name} and ${firstCard.name} switched positions!`,
                    'ability',
                    { cardId: card.id, swapped: [firstCard.name, lastCard.name] }
                );
            }
        }

        // ── Chaos Shuffle (50%) — all living cards reshuffled randomly ──
        // Rebuild livingIndices after potential Wild Swap, then splice each slot.
        if (Math.random() < 0.50) {
            livingIndices = [];
            for (var li = 0; li < team2.length; li++) {
                if (team2[li] && team2[li].hp > 0) { livingIndices.push(li); }
            }
            var living = livingIndices.map(function(i) { return team2[i]; });
            for (var j = living.length - 1; j > 0; j--) {
                var k    = Math.floor(Math.random() * (j + 1));
                var swap = living[j]; living[j] = living[k]; living[k] = swap;
            }
            // Write back with splice so Vue 2 detects each mutation
            livingIndices.forEach(function(origIdx, pos) { team2.splice(origIdx, 1, living[pos]); });

            flipNextCard();
            effects.add(card, ['powerup']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Chaos Shuffle: opponent's deck reshuffled!`,
                    'ability',
                    { cardId: card.id, newOrder: living.map(function(c) { return c.name; }) }
                );
            }
        }
    };

    // ── Johnson — Hoorah / Sacrificial Grenade ────────────────────────────────
    /**
     * Card: Johnson (97D2DEFA-75F6-4B95-8D7B-933BAF274713)
     * Ability ID: D4E842A0-F9A6-4591-B145-EB96CAEC8E4F
     *
     * onCrit — two branches:
     *
     * Sacrificial Grenade (below 15% HP):
     *   Deal 5000 bonus damage to the opponent, then Johnson kills himself.
     *   Takes priority — fires instead of Hoorah when condition is met.
     *
     * Hoorah (normal crits):
     *   Johnson gains +10 stamina.
     *   The next living teammate (by position) gains +5 accuracy.
     */
    window.abilities.onCrit['D4E842A0-F9A6-4591-B145-EB96CAEC8E4F'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1]; // Johnson
        const opponent = team2[index2];
        if (!card || !opponent || opponent.hp <= 0) { return; }

        const maxHp = card.originalHp || card.hp;
        const hpPct = (card.hp / maxHp) * 100;

        // ── Sacrificial Grenade — fires when below 15% HP ──────────────────
        if (hpPct < 15) {
            opponent.hp = Math.max(0, opponent.hp - 5000);
            effects.add(opponent, ['shakeY']);
            effects.add(card, ['glow-red']);

            // Johnson sacrifices himself
            card.hp = 0;

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Sacrificial Grenade: dealt 5000 bonus damage to ${opponent.name} — ${card.name} is gone!`,
                    'ability',
                    { cardId: card.id, bonusDamage: 5000 }
                );
            }
            return; // skip Hoorah
        }

        // ── Hoorah — normal crit ────────────────────────────────────────────
        // +10 stamina for Johnson
        card.currentStamina = Math.min(card.maxStamina || 100, (Number(card.currentStamina) || 0) + 10);

        // +5 accuracy for the next living teammate (by position)
        const nextTeammate = team1.find(function(c, i) { return i !== index1 && c && c.hp > 0; });
        if (nextTeammate) {
            nextTeammate.accuracy = (Number(nextTeammate.accuracy) || 0) + 5;
            nextTeammate.activeTraits = nextTeammate.activeTraits || {};
            nextTeammate.activeTraits.hoorahBuff = { label: 'Hoorah! +5 Acc', turnsLeft: 9999 };
            effects.add(nextTeammate, ['powerup']);
        }

        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Hoorah! +10 stamina${nextTeammate ? `, ${nextTeammate.name} gains +5 accuracy` : ''}`,
                'ability',
                { cardId: card.id, stamina: card.currentStamina, teammate: nextTeammate ? nextTeammate.name : null }
            );
        }
    };

    // ── Istria — Punish the Sloppy ────────────────────────────────────────────
    /**
     * Card: Istria (00BF8F29-626F-4588-853C-DEA1564984A1)
     * Ability ID: D9003529-7161-44D3-B2AE-C55144BB4585
     *
     * onMissed: Each time the opponent misses Istria, increment a miss counter
     * and update the badge on Istria's card.
     * At 4 total misses, reduce the attacker's pAtk and mAtk by 30% permanently.
     */
    window.abilities.onMissed['D9003529-7161-44D3-B2AE-C55144BB4585'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1]; // Istria (defender that was missed)
        const attacker = team2[index2]; // opponent who missed
        if (!attacker) { return; }

        // Increment miss counter on the attacker
        attacker._istria_missCount = (attacker._istria_missCount || 0) + 1;
        const missCount = attacker._istria_missCount;

        // Update badge on Istria showing current miss tally
        card.activeTraits = card.activeTraits || {};
        card.activeTraits.missTracker = { label: `Misses: ${missCount}/4`, turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Punish the Sloppy: ${attacker.name} has missed ${missCount}/4 times`,
                'ability',
                { cardId: card.id, missCount, attackerId: attacker.id }
            );
        }

        // At 4 misses — reduce attacker's attack by 30%
        if (missCount === 4) {
            if (attacker._basePAtk === undefined) { attacker._basePAtk = Number(attacker.pAtk) || 0; }
            if (attacker._baseMAtk === undefined) { attacker._baseMAtk = Number(attacker.mAtk) || 0; }

            attacker.pAtk = Math.round(attacker._basePAtk * 0.70);
            attacker.mAtk = Math.round(attacker._baseMAtk * 0.70);

            card.activeTraits.missTracker = { label: 'Punished! (4 misses)', turnsLeft: 9999 };
            effects.add(attacker, ['shakeY']);
            effects.add(card, ['powerup']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Punish the Sloppy: ${attacker.name} reached 4 misses — attack reduced by 30%!`,
                    'ability',
                    { cardId: card.id, pAtk: attacker.pAtk, mAtk: attacker.mAtk }
                );
            }
        }
    };

    // ── Incarnis — Mirror Crit ────────────────────────────────────────────────
    /**
     * Card: Incarnis (2E0FDF5A-C478-4C38-962B-97B126627E0A)
     * Ability ID: 6E0DDAA4-362D-4AD0-B728-FC4DB2980665
     *
     * Ability 1 — damaged (reflect):
     *   When Incarnis receives a critical hit, reflect 1000 damage back on the attacker.
     *
     * Ability 2 — onCrit (splash):
     *   When Incarnis lands a critical hit, deal 500 bonus damage to the current opponent
     *   AND 500 damage to the next living card on the opposing team.
     */

    // damaged — reflect 1000 on incoming crits
    window.abilities.damaged['6E0DDAA4-362D-4AD0-B728-FC4DB2980665'] = function(team1, team2, index1, index2, _damage, _events, isCrit, logFn) {
        if (!isCrit) { return; }
        const card     = team1[index1]; // defender (Incarnis)
        const attacker = team2[index2];
        if (!attacker || attacker.hp <= 0) { return; }

        attacker.hp = Math.max(0, attacker.hp - 1000);
        effects.add(attacker, ['shakeY']);
        effects.add(card, ['glow-blue']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Mirror Crit: reflected 1000 damage back at ${attacker.name}!`,
                'ability',
                { cardId: card.id, reflected: 1000, attackerId: attacker.id }
            );
        }
    };

    // onCrit — splash 500 to current opponent + 500 to next living card
    window.abilities.onCrit['6E0DDAA4-362D-4AD0-B728-FC4DB2980665'] = function(team1, team2, index1, index2, _events, logFn) {
        const card     = team1[index1]; // Incarnis (attacker)
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }

        // 500 bonus damage to current opponent
        opponent.hp = Math.max(0, opponent.hp - 500);
        effects.add(opponent, ['shakeY']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Mirror Crit: +500 splash damage on ${opponent.name}`,
                'ability',
                { cardId: card.id, splash: 500, targetId: opponent.id }
            );
        }

        // 500 damage to the next living card on the opposing team
        const nextCard = team2.find(function(c, i) { return i !== index2 && c && c.hp > 0; });
        if (nextCard) {
            nextCard.hp = Math.max(0, nextCard.hp - 500);
            effects.add(nextCard, ['shakeY']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Mirror Crit: +500 splash damage bleeds through to ${nextCard.name}`,
                    'ability',
                    { cardId: card.id, splash: 500, targetId: nextCard.id }
                );
            }
        }
    };

    // ── Hitman Agent 47 — First Strike ───────────────────────────────────────
    /**
     * Card: Hitman Agent 47 (2E84E26E-6948-430E-BF87-6C37149B2816)
     * Ability ID: 39AB310F-98FA-4A24-81A9-136E73D08CB4
     *
     * Passive: When a new opponent card enters battle, deal 20% bonus damage
     * for the duration of that matchup.
     * Detects card change by comparing opponent.id to the last seen opponent ID.
     * The first opponent also counts as "new".
     */
    window.abilities.passive['39AB310F-98FA-4A24-81A9-136E73D08CB4'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        // Detect new opponent (including the very first one — _lastOpponentId starts undefined)
        if (card._lastOpponentId !== opponent.id) {
            card._lastOpponentId = opponent.id;

            card.pAtk = Math.round(card._basePAtk * 1.20);
            card.mAtk = Math.round(card._baseMAtk * 1.20);

            card.activeTraits = card.activeTraits || {};
            card.activeTraits.firstStrike = { label: 'First Strike', turnsLeft: 9999 };

            effects.add(card, ['powerup']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s First Strike: new target acquired — +20% damage (pAtk ${card.pAtk}, mAtk ${card.mAtk})`,
                    'ability',
                    { cardId: card.id, pAtk: card.pAtk, mAtk: card.mAtk, targetId: opponent.id }
                );
            }
        }
    };

    // ── Helsing — Bleed Hunter ───────────────────────────────────────────────
    /**
     * Card: Helsing (AC94E32D-B0D4-481D-9395-6CD47B7AF3CF)
     * Ability ID: D3F1C15E-6296-48E4-904E-7B05388E1892
     *
     * Passive: While Helsing is alive, set opponent._bleedDmgMult = 1.20 so
     * that traits.js applies 20% bonus to every bleeding tick on that opponent.
     * Cleared when Helsing is gone or the opponent is not the current target.
     *
     * Requires traits.js to read card._bleedDmgMult in processOnAct / processOnHit.
     */
    window.abilities.passive['D3F1C15E-6296-48E4-904E-7B05388E1892'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        const opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        const opponentBleeds = !!(window.traits && traits.has(opponent, 'bleeding'));

        if (opponentBleeds) {
            if (opponent._bleedDmgMult !== 1.20) {
                opponent._bleedDmgMult = 1.20;
                card.activeTraits = card.activeTraits || {};
                card.activeTraits.bleedHunter = { label: 'Bleed Hunter', turnsLeft: 9999 };
                effects.add(card, ['glow-red']);
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Bleed Hunter: bleeding damage on ${opponent.name} increased by 20%`,
                        'ability',
                        { cardId: card.id }
                    );
                }
            }
        } else {
            // Opponent not bleeding — remove the multiplier and badge
            if (opponent._bleedDmgMult !== undefined) { delete opponent._bleedDmgMult; }
            if (card.activeTraits) { delete card.activeTraits.bleedHunter; }
        }
    };

    // ── Hela — Arcane Bulwark ─────────────────────────────────────────────────
    /**
     * Card: Hela (FA2BE3C5-F789-4E7C-9626-E6B258DA3079)
     * Ability ID: 71A9504C-F486-4D44-BFE8-DA54D354C5DA
     *
     * Passive: If the opponent's mAtk > pAtk (primarily magic attacker),
     * Hela's armour value is added on top of her ward.
     * Reverts to base ward when the condition no longer holds.
     */
    window.abilities.passive['71A9504C-F486-4D44-BFE8-DA54D354C5DA'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        const opponent = opposingTeam[opposingIndex];

        // Store base ward once
        if (card._baseWard === undefined) { card._baseWard = Number(card.ward) || 0; }

        const isMagicPrimary = opponent && opponent.hp > 0 && (Number(opponent.mAtk) || 0) > (Number(opponent.pAtk) || 0);

        if (isMagicPrimary) {
            const boostedWard = card._baseWard + (Number(card.armor) || 0);
            if (card.ward !== boostedWard) {
                var _origHelaWard = card.ward;
                card.ward = boostedWard;
                window.flashStatGlow(card, 'ward', _origHelaWard, boostedWard);
                card.activeTraits = card.activeTraits || {};
                card.activeTraits.arcaneBulwark = { label: 'Arcane Bulwark', turnsLeft: 9999 };
                effects.add(card, ['glow-blue']);
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Arcane Bulwark: armor added to ward (ward → ${card.ward})`,
                        'ability',
                        { cardId: card.id, ward: card.ward, armor: card.armor }
                    );
                }
            }
        } else {
            if (card.ward !== card._baseWard) {
                card.ward = card._baseWard;
                if (card.activeTraits) { delete card.activeTraits.arcaneBulwark; }
            }
        }
    };

    // ── Grimes — Ironclad Will ───────────────────────────────────────────────
    /**
     * Card: Grimes (2A52BDD9-136A-467C-9A87-C50C68F8984D)
     * Ability ID: B9168756-4C0A-4661-96A8-7E2A0CB7CEA8
     *
     * Passive: 25% chance each turn to lock armour, preventing it from degrading.
     * When locked, any armour reduction applied during the previous turn is undone
     * at the start of the next turn by restoring the snapshotted value.
     *
     * Sequence per turn:
     *   1. If lock was active: restore card.armor to the locked snapshot.
     *   2. Roll 25% — if hit, snapshot current armor and mark as locked.
     *   3. If miss, clear the lock (armor can degrade freely this turn).
     */
    window.abilities.passive['B9168756-4C0A-4661-96A8-7E2A0CB7CEA8'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        // Step 1 — restore armor if lock was active from last turn
        if (card._armorLocked && card._lockedArmorValue !== undefined) {
            if (card.armor !== card._lockedArmorValue) {
                card.armor = card._lockedArmorValue;
                if (typeof logFn === 'function') {
                    logFn(
                        `${card.name}'s Ironclad Will: armour lock restored armor to ${card.armor}`,
                        'ability',
                        { cardId: card.id, armor: card.armor }
                    );
                }
            }
        }

        // Step 2 — roll for this turn's lock
        if (Math.random() < 0.95) {
            card._lockedArmorValue = Number(card.armor) || 0;
            card._armorLocked     = true;

            card.activeTraits = card.activeTraits || {};
            card.activeTraits.armorLock = { label: 'Armor Locked', turnsLeft: 9999 };

            effects.add(card, ['glow-green']);

            if (typeof logFn === 'function') {
                logFn(
                    `${card.name}'s Ironclad Will: armour locked at ${card._lockedArmorValue} this turn`,
                    'ability',
                    { cardId: card.id, lockedArmor: card._lockedArmorValue }
                );
            }
        } else {
            // Lock not active this turn — armour can degrade freely
            card._armorLocked = false;
            if (card.activeTraits && card.activeTraits.armorLock) {
                delete card.activeTraits.armorLock;
            }
        }
    };

    // ── Green Goblin — Chaos Serum ───────────────────────────────────────────
    /**
     * Card: Green Goblin (74BB931F-2927-45FB-8C80-951C43647CAE)
     * Ability ID: A5A03370-4CBC-40DF-866C-26D54DF67ACE
     *
     * Move: 20% chance to inflict a random negative status effect on the opponent.
     * Applies via traits.apply() — respects immunity and existing durations.
     */
    var _goblinNegativeTraits = [
        'slowed', 'staggered', 'pacified', 'shocked', 'withered',
        'broken', 'dominated', 'disarmed', 'hallucinating',
        'bleeding', 'frozen', 'burning', 'poisoned', 'stunned', 'weakened', 'blinded'
    ];
    window.abilities.move['A5A03370-4CBC-40DF-866C-26D54DF67ACE'] = function(team1, team2, index1, index2, _events, logFn) {
        // NOTE: Change 0.98 back to 0.20 after testing
        if (Math.random() >= 0.20) { return; }

        const card     = team1[index1];
        const opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }
        if (!window.traits || typeof traits.apply !== 'function') { return; }

        const traitName = _goblinNegativeTraits[Math.floor(Math.random() * _goblinNegativeTraits.length)];
        traits.apply(opponent, traitName);

        effects.add(opponent, ['glow-red']);

        if (typeof logFn === 'function') {
            logFn(
                `${card.name}'s Chaos Serum inflicts ${traitName} on ${opponent.name}!`,
                'ability',
                { cardId: card.id, trait: traitName }
            );
        }
    };

    // ── Crowley — Lingering Curse ─────────────────────────────────────────────
    /**
     * Card: Crowley (63AE1784-E31A-41F5-89DD-75CB8EA0F36C)
     * Ability ID: 51BB38A7-65C1-444F-B282-EE23CE5ED414
     *
     * Passive: Opponent's negative status effects last one turn longer.
     * When a negative trait first appears on an opponent, Crowley extends it by +1 turn.
     * The extension is applied once per trait application — tracked via
     * opponent['_crowleyExt_' + card.id + '_' + traitName] so it resets if the
     * trait expires and is re-applied.
     * Multiple Crowleys each independently add +1 (stacks additively).
     */
    var _crowleyNegativeTraits = ['slowed', 'bleeding', 'frozen', 'burning', 'dominated', 'poisoned', 'stunned', 'weakened', 'blinded'];
    window.abilities.passive['51BB38A7-65C1-444F-B282-EE23CE5ED414'] = function(card, _cardIndex, _team, opposingTeam, _opposingIndex, _events, logFn) {
        opposingTeam.forEach(function(opponent) {
            if (!opponent || opponent.hp <= 0 || !opponent.activeTraits) { return; }

            _crowleyNegativeTraits.forEach(function(traitName) {
                var flagKey = '_crowleyExt_' + card.id + '_' + traitName;
                var trait = opponent.activeTraits[traitName];

                if (!trait || trait.turnsLeft <= 0) {
                    // Trait not active — clear extension flag so next application can be extended
                    if (opponent[flagKey]) { delete opponent[flagKey]; }
                    return;
                }

                // Trait is active and not yet extended this application — extend by 1
                if (!opponent[flagKey]) {
                    trait.turnsLeft += 1;
                    opponent[flagKey] = true;
                    if (typeof logFn === 'function') {
                        logFn(
                            `${card.name}'s Lingering Curse: ${opponent.name}'s ${traitName} extended by 1 turn (${trait.turnsLeft} turns left)`,
                            'ability',
                            { cardId: card.id, trait: traitName, turnsLeft: trait.turnsLeft }
                        );
                    }
                }
            });
        });
    };

    // ── Krython — Ability Suppressor ─────────────────────────────────────────
    // 99% chance each turn the opponent's ability is nulled.
    // NOTE: Change 0.99 back to 0.60 after testing.
    window.abilities.passive['00784FE9-E3F7-4753-8655-1AD86E65C0BA'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        var opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        // NOTE: Change 0.99 back to 0.60 after testing
        var rollHit = Math.random() < 0.60;

        if (rollHit) {
            // Backup the opponent's real abilityKey once, then null it
            if (opponent._krythonOriginalKey === undefined) {
                opponent._krythonOriginalKey = opponent.abilityKey !== undefined ? opponent.abilityKey : null;
            }
            opponent.abilityKey = null;

            opponent.activeTraits.abilitySuppress = { label: 'Ability Disabled', turnsLeft: 9999 };
            card.activeTraits.abilitySuppressor = { label: 'Suppressor Active', turnsLeft: 9999 };
            effects.add(card, ['glow-red']);

            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Ability Suppressor: ' + opponent.name + '\'s ability is disabled this turn',
                    'ability',
                    { cardId: card.id, opponentId: opponent.id }
                );
            }
        } else {
            // Roll failed — restore opponent's ability if we had disabled it
            if (opponent._krythonOriginalKey !== undefined) {
                opponent.abilityKey = opponent._krythonOriginalKey;
                delete opponent._krythonOriginalKey;
            }
            delete opponent.activeTraits.abilitySuppress;
            delete card.activeTraits.abilitySuppressor;
        }
    };

    // ── Kyo — Executioner's Mark ─────────────────────────────────────────────
    // Guarantees a critical hit whenever the opponent has 2 or more active
    // negative status effects. Restored to baseline when condition is not met.
    var _kyoDebuffList = ['slowed','staggered','pacified','shocked','withered','broken','dominated','disarmed','hallucinating','bleeding','frozen','burning','poisoned','stunned','weakened','blinded'];

    window.abilities.passive['30314486-A682-4817-8DF3-8D20D16E887D'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        // Snapshot baseline critChance once
        if (card._baseCritChance === undefined) {
            card._baseCritChance = Number(card.critChance) || 0;
        }

        // Count how many negative debuffs Kyo herself currently has active
        var activeDebuffCount = 0;
        if (card.activeTraits) {
            for (var i = 0; i < _kyoDebuffList.length; i++) {
                var t = card.activeTraits[_kyoDebuffList[i]];
                if (t && t.turnsLeft > 0) { activeDebuffCount++; }
            }
        }

        if (activeDebuffCount >= 2) {
            if (card.critChance !== 100) {
                card.critChance = 100;
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Executioner\'s Mark: guaranteed crit (' + activeDebuffCount + ' debuffs on self)',
                        'ability',
                        { cardId: card.id, debuffCount: activeDebuffCount }
                    );
                }
            }
            card.activeTraits.executionersMark = { label: 'Crit Guaranteed (' + activeDebuffCount + ' debuffs)', turnsLeft: 9999 };
        } else {
            // Condition not met — restore baseline
            if (card.critChance !== card._baseCritChance) {
                card.critChance = card._baseCritChance;
            }
            delete card.activeTraits.executionersMark;
        }
    };

    // ── Lara Croft — Counter Instinct ────────────────────────────────────────
    // Tracks consecutive hits the opponent lands on Lara.
    // At 4 consecutive hits: opponent pAtk & mAtk reduced by 20%.
    // A miss resets the counter and restores the reduction.

    // damaged — opponent hit Lara; increment consecutive counter
    window.abilities.damaged['626FF608-06F1-49A2-B3B7-DEBAC7ED130F'] = function(team1, team2, index1, index2, _damage, _events, _isCrit, logFn) {
        var card     = team1[index1]; // Lara
        var attacker = team2[index2];
        if (!attacker || attacker.hp <= 0) { return; }

        // Reset counter if a new attacker is facing Lara
        if (card._laraLastAttackerId !== attacker.id) {
            card._laraLastAttackerId = attacker.id;
            card._laraConsecHits     = 0;
            delete card._laraDebuffAppliedTo;
        }

        card._laraConsecHits = (card._laraConsecHits || 0) + 1;

        if (card._laraConsecHits < 4) {
            // Progress badge
            card.activeTraits.counterInstinct = { label: 'Counter ' + card._laraConsecHits + '/4', turnsLeft: 9999 };
            return;
        }

        // 4th hit (or beyond) — apply reduction once per attacker
        if (card._laraDebuffAppliedTo !== attacker.id) {
            card._laraDebuffAppliedTo   = attacker.id;
            attacker._laraOrigPAtk      = Number(attacker.pAtk) || 0;
            attacker._laraOrigMAtk      = Number(attacker.mAtk) || 0;
            attacker.pAtk               = Math.round(attacker._laraOrigPAtk * 0.80);
            attacker.mAtk               = Math.round(attacker._laraOrigMAtk * 0.80);
            attacker.activeTraits.laraCounter = { label: 'Countered \u221220% Atk', turnsLeft: 9999 };
            effects.add(card, ['powerup']);
            effects.add(attacker, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Counter Instinct: ' + attacker.name + '\'s attack reduced by 20% after 4 consecutive hits',
                    'ability',
                    { cardId: card.id, attackerId: attacker.id }
                );
            }
        }
        card.activeTraits.counterInstinct = { label: 'Counter ACTIVE \u2605', turnsLeft: 9999 };
    };

    // onMissed — opponent missed Lara; reset consecutive counter and restore debuff
    window.abilities.onMissed['626FF608-06F1-49A2-B3B7-DEBAC7ED130F'] = function(team1, team2, index1, index2, _events, _logFn) {
        var card     = team1[index1]; // Lara
        var attacker = team2[index2];

        // Restore attacker's attack if we had debuffed them
        if (attacker && card._laraDebuffAppliedTo === attacker.id) {
            if (attacker._laraOrigPAtk !== undefined) { attacker.pAtk = attacker._laraOrigPAtk; delete attacker._laraOrigPAtk; }
            if (attacker._laraOrigMAtk !== undefined) { attacker.mAtk = attacker._laraOrigMAtk; delete attacker._laraOrigMAtk; }
            delete attacker.activeTraits.laraCounter;
        }

        // Reset streak tracking
        card._laraConsecHits = 0;
        delete card._laraDebuffAppliedTo;
        delete card.activeTraits.counterInstinct;
    };

    // ── Liu Kang — Dragon Combo ──────────────────────────────────────────────
    // Two consecutive critical hits drain 50% of the opponent's current stamina.
    // A non-crit hit resets the consecutive counter.
    window.abilities.onHit['5248CFBE-C127-4078-B73D-733821CA8774'] = function(team1, team2, index1, index2, _damage, isCrit, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        if (!isCrit) {
            // Non-crit breaks the streak
            card._liuConsecCrits = 0;
            delete card.activeTraits.dragonCombo;
            return;
        }

        card._liuConsecCrits = (card._liuConsecCrits || 0) + 1;

        if (card._liuConsecCrits < 2) {
            card.activeTraits.dragonCombo = { label: 'Dragon Combo 1/2', turnsLeft: 9999 };
            return;
        }

        // 2nd consecutive crit — drain 50% of opponent's current stamina
        if (opponent && opponent.hp > 0) {
            if (opponent._throttleStaminaImmune) {
                opponent._throttleStaminaBlockedThisTick = true;
                effects.add(opponent, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(opponent.name + '\'s Overdrive: Dragon Combo stamina drain blocked!', 'ability', { cardId: opponent.id });
                }
                return;
            }
            var drain = Math.floor((Number(opponent.currentStamina) || 0) * 0.50);
            opponent.currentStamina = Math.max(0, (Number(opponent.currentStamina) || 0) - drain);
            effects.add(card, ['powerup']);
            effects.add(opponent, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Dragon Combo: 2 consecutive crits — drained ' + drain + ' stamina from ' + opponent.name,
                    'ability',
                    { cardId: card.id, drained: drain }
                );
            }
        }

        // Reset after trigger — next streak starts fresh
        card._liuConsecCrits = 0;
        card.activeTraits.dragonCombo = { label: 'Dragon Combo \u2605 Fired!', turnsLeft: 9999 };
    };

    // ── Magneto — Magnetic Fury ──────────────────────────────────────────────
    // Each time Magneto takes physical damage (attacker has pAtk > 0),
    // his magic attack permanently increases by 100.
    window.abilities.damaged['56B4046A-C5AB-47BF-87F5-FDCA06FFD8F6'] = function(team1, team2, index1, index2, _damage, _events, _isCrit, logFn) {
        var card     = team1[index1]; // Magneto
        var attacker = team2[index2];

        // Only trigger on physical damage (attacker has a physical component)
        if (!attacker || (Number(attacker.pAtk) || 0) <= 0) { return; }

        // Snapshot baseline mAtk once
        if (card._baseMAtk === undefined) {
            card._baseMAtk = Number(card.mAtk) || 0;
        }

        var _origMagnetoMAtk = Number(card.mAtk) || 0;
        card.mAtk = _origMagnetoMAtk + 100;
        window.flashStatGlow(card, 'mAtk', _origMagnetoMAtk, card.mAtk);

        var bonus = card.mAtk - card._baseMAtk;
        card.activeTraits.magneticFury = { label: 'Magnetic Fury +' + bonus + ' mAtk', turnsLeft: 9999 };
        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Magnetic Fury: physical hit absorbed — mAtk +100 (total bonus +' + bonus + ')',
                'ability',
                { cardId: card.id, mAtk: card.mAtk }
            );
        }
    };

    // ── Maw — Void Leech ─────────────────────────────────────────────────────
    // Every time an opponent card regenerates stamina or mana, they lose 500 HP.
    // The passive stamps _mawRegenDrain = true onto every living opponent card.
    // battleArena.js::regenerateResource() reads that flag and drains HP.
    window.abilities.passive['A5103A6E-7EDC-405C-A39B-BDBC7D16C906'] = function(card, _cardIndex, _team, opposingTeam, _opposingIndex, _events, _logFn) {
        for (var i = 0; i < opposingTeam.length; i++) {
            var opp = opposingTeam[i];
            if (!opp || opp.hp <= 0) { continue; }
            opp._mawRegenDrain = true;
        }
        card.activeTraits.voidLeech = { label: 'Void Leech: Regen = -500 HP', turnsLeft: 9999 };
        effects.add(card, ['glow-red']);
    };

    // ── Milania — Untouchable ────────────────────────────────────────────────
    // Every 3 opponent attacks that are NOT critical hits, Milania gains +10% pAtk.
    // Misses also count as non-crit attacks. A crit resets the counter (not the boost).

    function _milaniaCheckTrigger(card, logFn) {
        if (card._basePAtk === undefined) {
            card._basePAtk = Number(card.pAtk) || 0;
        }
        card._milaniaNoCritCount = (card._milaniaNoCritCount || 0) + 1;
        card.activeTraits.untouchable = { label: 'Untouchable ' + card._milaniaNoCritCount + '/3', turnsLeft: 9999 };

        if (card._milaniaNoCritCount >= 3) {
            card._milaniaNoCritCount = 0;
            card._milaniaBoostStacks = (card._milaniaBoostStacks || 0) + 1;
            var _origMilaniaPAtk = Number(card.pAtk) || 0;
            card.pAtk = Math.round(card._basePAtk * (1 + 0.10 * card._milaniaBoostStacks));
            window.flashStatGlow(card, 'pAtk', _origMilaniaPAtk, card.pAtk);
            var totalBonus = card._milaniaBoostStacks * 10;
            card.activeTraits.untouchable = { label: 'Untouchable +' + totalBonus + '% pAtk', turnsLeft: 9999 };
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Untouchable: opponent landed no crit in 3 attacks — pAtk +10% (total +' + totalBonus + '%)',
                    'ability',
                    { cardId: card.id, pAtk: card.pAtk, stacks: card._milaniaBoostStacks }
                );
            }
        }
    }

    // damaged — opponent hit Milania; crit resets counter, non-crit increments it
    window.abilities.damaged['55A4244C-96CB-42AC-8D75-746AAB09436F'] = function(team1, _team2, index1, _index2, _damage, _events, isCrit, logFn) {
        var card = team1[index1];
        if (isCrit) {
            card._milaniaNoCritCount = 0;
            delete card.activeTraits.untouchable;
            return;
        }
        _milaniaCheckTrigger(card, logFn);
    };

    // onMissed — opponent missed Milania; counts as a non-crit attack
    window.abilities.onMissed['55A4244C-96CB-42AC-8D75-746AAB09436F'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        _milaniaCheckTrigger(card, logFn);
    };

    // ── Nash — Killing Momentum ──────────────────────────────────────────────
    // Defeating an opponent card with a critical hit activates +1000 HP regen per turn.
    //
    // NOTE: card2.hp is updated inside a setTimeout in damageTaken, so opponent.hp
    // is still the pre-damage value when onCrit fires. Kill detection must use onKill.
    // Pattern: move resets the crit flag → onCrit sets it → onKill reads and clears it.

    // move — reset the pending crit flag at the start of each attack
    window.abilities.move['EEF2087C-F2A1-4FC9-9248-2A35B5747BFB'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        team1[index1]._nashCritThisAttack = false;
    };

    // onCrit — record that this attack was a crit
    window.abilities.onCrit['EEF2087C-F2A1-4FC9-9248-2A35B5747BFB'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        team1[index1]._nashCritThisAttack = true;
    };

    // onKill — if the killing blow was a crit, activate regen
    window.abilities.onKill['EEF2087C-F2A1-4FC9-9248-2A35B5747BFB'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        if (!card._nashCritThisAttack) { return; } // kill was not a crit

        card._nashRegenActive = true;
        card.activeTraits.killingMomentum = { label: 'Killing Momentum +1000 HP/turn', turnsLeft: 9999 };
        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Killing Momentum activated: +1000 HP regeneration per turn',
                'ability',
                { cardId: card.id }
            );
        }
    };

    // passive — apply +1000 HP regen each turn while Killing Momentum is active
    window.abilities.passive['EEF2087C-F2A1-4FC9-9248-2A35B5747BFB'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (!card._nashRegenActive) { return; }

        var maxHp = card.originalHp || card.maxHp || card.hp;
        card.hp = Math.min(maxHp, card.hp + 1000);
        effects.add(card, ['glow-green']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Killing Momentum: +1000 HP (current: ' + card.hp + ')',
                'ability',
                { cardId: card.id, hp: card.hp }
            );
        }
    };

    // ── Negan — Lucille ──────────────────────────────────────────────────────
    // For every 10 stamina an opponent regenerates, they take 400 damage.
    // Uses a running accumulator per opponent card so fractional regen ticks
    // eventually add up (e.g. 6 stamina/turn → 400 dmg every ~2 turns).
    // The passive stamps _neganStaminaBurn on all living opponents.
    // regenerateResource() in battleArena.js reads the flag and applies the drain.
    window.abilities.passive['AA24622F-D4B9-4037-A554-6A071C19931D'] = function(card, _cardIndex, _team, opposingTeam, _opposingIndex, _events, _logFn) {
        for (var i = 0; i < opposingTeam.length; i++) {
            var opp = opposingTeam[i];
            if (!opp || opp.hp <= 0) { continue; }
            opp._neganStaminaBurn = true;
        }
        card.activeTraits.lucille = { label: 'Lucille: Regen = 400 dmg/10 sta', turnsLeft: 9999 };
        effects.add(card, ['glow-red']);
    };

    // ── Noctis — Soul Harvest ────────────────────────────────────────────────
    // On kill: 25% chance to gain 1200 HP (capped at max).
    // On kill: 25% chance to drain 1000 HP from the next living opponent card.
    // Both rolls are independent — neither, one, or both can trigger on the same kill.
    window.abilities.onKill['9E42CC74-E629-4AFB-97E6-4DB6063FBCB5'] = function(team1, team2, index1, _index2, _events, logFn) {
        var card = team1[index1];

        // Roll 1 — Heal Noctis 1200 HP (25%)
        if (Math.random() < 0.99) {
            var maxHp   = card.originalHp || card.hp;
            var healed  = Math.min(1200, maxHp - card.hp);
            card.hp     = Math.min(maxHp, card.hp + 1200);
            effects.add(card, ['glow-green']);
            card.activeTraits.soulHarvest = { label: 'Soul Harvest +' + healed + ' HP', turnsLeft: 9999 };
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Soul Harvest: absorbed ' + healed + ' HP from defeated opponent',
                    'ability',
                    { cardId: card.id, healed: healed }
                );
            }
        }

        // Roll 2 — Drain 1000 HP from next living opponent card (25%)
        if (Math.random() < 0.25) {
            var nextOpp = null;
            for (var i = 0; i < team2.length; i++) {
                if (team2[i] && team2[i].hp > 0) { nextOpp = team2[i]; break; }
            }
            if (nextOpp) {
                nextOpp.hp = Math.max(0, nextOpp.hp - 1000);
                effects.add(nextOpp, ['shakeY']);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Soul Harvest: drained 1000 HP from ' + nextOpp.name,
                        'ability',
                        { cardId: card.id, targetId: nextOpp.id }
                    );
                }
            }
        }
    };

    // ── Noctyl — Arcane Bulwark ──────────────────────────────────────────────
    // Each time Noctyl receives magic damage (attacker mAtk > 0):
    //   armor +15% of base (stacks additively)
    //   ward  +15% of base (stacks additively)
    window.abilities.damaged['6B106087-C4E0-47E1-A967-7868A2E73FB0'] = function(team1, team2, index1, index2, _damage, _events, _isCrit, logFn) {
        var card     = team1[index1]; // Noctyl
        var attacker = team2[index2];

        // Only trigger when the attacker's mAtk is higher than their pAtk (magic-primary)
        if (!attacker || (Number(attacker.mAtk) || 0) <= (Number(attacker.pAtk) || 0)) { return; }

        // Snapshot baselines once
        if (card._baseArmor === undefined) { card._baseArmor = Number(card.armor) || 0; }
        if (card._baseWard  === undefined) { card._baseWard  = Number(card.ward)  || 0; }

        card._noctylMagicHits = (card._noctylMagicHits || 0) + 1;

        var armorBonus = Math.round(card._baseArmor * 0.15 * card._noctylMagicHits);
        var wardBonus  = Math.round(card._baseWard  * 0.15 * card._noctylMagicHits);

        var _origNoctylArmor = card.armor;
        var _origNoctylWard  = card.ward;
        card.armor = card._baseArmor + armorBonus;
        card.ward  = card._baseWard  + wardBonus;
        window.flashStatGlow(card, 'armor', _origNoctylArmor, card.armor);
        window.flashStatGlow(card, 'ward',  _origNoctylWard,  card.ward);

        var stackPct = card._noctylMagicHits * 15;
        card.activeTraits.arcaneBulwark = { label: 'Arcane Bulwark +' + stackPct + '% armor/ward', turnsLeft: 9999 };
        effects.add(card, ['glow-blue']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Arcane Bulwark: magic hit absorbed — armor +' + armorBonus + ', ward +' + wardBonus + ' (stack ' + card._noctylMagicHits + ')',
                'ability',
                { cardId: card.id, armor: card.armor, ward: card.ward }
            );
        }
    };

    // ── Parallax — Momentum Shift ────────────────────────────────────────────
    // Slowed is converted into a buff for Parallax:
    //   +30% speed and +10% pAtk/mAtk for the duration of the slowed effect.
    // The passive stamps _slowedImmune = true each turn.
    // traits.js reads this flag in the slowed apply/remove cases.
    window.abilities.passive['33C7A7CB-47B6-40B4-825A-3BC2987D6CD4'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, _logFn) {
        card._slowedImmune = true;
        // Show badge only while actually slowed (so player knows the conversion is active)
        if (card.activeTraits && card.activeTraits.slowed && card.activeTraits.slowed.turnsLeft > 0) {
            card.activeTraits.momentumShift = { label: 'Momentum Shift: Slowed \u2192 Buff', turnsLeft: 9999 };
            effects.add(card, ['powerup']);
        } else {
            delete card.activeTraits.momentumShift;
        }
    };

    // ── Phase — Displacement ─────────────────────────────────────────────────
    // 60% chance on move to send the current opponent card to the back of the queue.
    // Uses splice (Vue-reactive) to avoid stale index references.
    // Current attack still hits the displaced card; the NEXT attack faces the new front card.
    window.abilities.move['2115D5BB-014A-4C53-BF09-88B98D952471'] = function(team1, team2, index1, index2, _events, logFn) {
        var card = team1[index1];

        if (Math.random() >= 0.60) { return; } // 40% — nothing happens

        var displaced = team2[index2];
        if (!displaced || displaced.hp <= 0) { return; }

        // Count living cards — no point displacing if opponent has only one alive
        var livingCount = 0;
        for (var i = 0; i < team2.length; i++) {
            if (team2[i] && team2[i].hp > 0) { livingCount++; }
        }
        if (livingCount < 2) { return; }

        // Remove from current position and push to the back (both Vue-reactive)
        team2.splice(index2, 1);
        team2.splice(team2.length, 0, displaced);

        // Flip the new front-line card so the player knows who they're facing
        setTimeout(function() {
            var newFront = team2[index2] || team2[0];
            if (newFront && newFront.id) {
                $('#' + newFront.id).find('.playing-card').addClass('flipped');
            }
        }, 0);

        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Displacement: ' + displaced.name + ' sent to the back of the queue',
                'ability',
                { cardId: card.id, displacedId: displaced.id }
            );
        }
    };

    // ── Phasora — Iron Shell ─────────────────────────────────────────────────
    // 50% chance each turn to lock armor so it cannot degrade.
    // Pattern mirrors Grimes — save current armor value and restore it next tick if locked.
    window.abilities.passive['D8E15742-F07E-418E-9AA4-A61364BB420F'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, _logFn) {
        // Step 1: if locked last turn, restore armor to the saved value
        if (card._armorLocked && card._lockedArmorValue !== undefined) {
            if (card.armor !== card._lockedArmorValue) {
                card.armor = card._lockedArmorValue;
            }
        }

        // Step 2: roll 50% to lock for this turn
        if (Math.random() < 0.99) {
            card._lockedArmorValue = Number(card.armor) || 0;
            card._armorLocked = true;
            card.activeTraits.ironShell = { label: 'Iron Shell: Armor Locked', turnsLeft: 9999 };
            effects.add(card, ['glow-green']);
        } else {
            card._armorLocked = false;
            delete card.activeTraits.ironShell;
        }
    };

    // ── Pheonix — Ember Rise ─────────────────────────────────────────────────
    // Every 3rd critical hit regenerates 2000 HP (capped at originalHp).
    window.abilities.onHit['DB29EC8C-41BE-4A9A-8CF8-962C3191ABCD'] = function(team1, _team2, index1, _index2, _damage, isCrit, _events, logFn) {
        var card = team1[index1];
        if (!isCrit) { return; }

        card._pheonixCritCount = (card._pheonixCritCount || 0) + 1;

        if (card._pheonixCritCount % 3 !== 0) {
            card.activeTraits.emberRise = { label: 'Ember Rise ' + card._pheonixCritCount + '/3', turnsLeft: 9999 };
            return;
        }

        // Every 3rd crit — heal 2000 HP
        var maxHp = card.originalHp || card.hp;
        card.hp   = Math.min(maxHp, card.hp + 2000);
        effects.add(card, ['glow-green']);
        card.activeTraits.emberRise = { label: 'Ember Rise \u2605 +2000 HP', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Ember Rise: 3rd critical hit — regenerated 2000 HP (total crits: ' + card._pheonixCritCount + ')',
                'ability',
                { cardId: card.id, hp: card.hp }
            );
        }
    };

    // ── Pinch — Endurance Protocol ───────────────────────────────────────────
    // When armor drops below 60% of its base value, physical attacks cost no stamina.
    // battleArena.js reads card._pinchStaminaFree to skip the deduction and force
    // _hasStaminaForAttack = true so physical damage is never penalised.
    window.abilities.passive['2EBEF1D3-A1C9-4AD9-88FD-08E7FF65B4FC'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._baseArmor === undefined) {
            card._baseArmor = Number(card.armor) || 0;
        }

        var currentArmor = Number(card.armor) || 0;
        var threshold    = card._baseArmor * 0.60;
        var activated    = currentArmor < threshold;

        if (activated) {
            if (!card._pinchStaminaFree) {
                card._pinchStaminaFree = true;
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Endurance Protocol: armor below 60% — stamina no longer used for physical attacks',
                        'ability',
                        { cardId: card.id, armor: currentArmor, threshold: threshold }
                    );
                }
            }
            card.activeTraits.enduranceProtocol = { label: 'Endurance Protocol: Free pAtk', turnsLeft: 9999 };
        } else {
            card._pinchStaminaFree = false;
            delete card.activeTraits.enduranceProtocol;
        }
    };

    // ── Plague Doctor — Antidote ─────────────────────────────────────────────
    // When Plague Doctor is poisoned, the poison tick heals 450 HP instead of dealing damage.
    // The passive stamps _poisonConvertsToHeal = true; traits.js reads it in the poison tick.
    window.abilities.passive['47760ECB-8BFA-4E5A-8CB4-874AE4B6AA66'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, _logFn) {
        card._poisonConvertsToHeal = true;
        if (window.traits && traits.has(card, 'poisoned')) {
            card.activeTraits.antidote = { label: 'Antidote: Poison \u2192 Heal', turnsLeft: 9999 };
        } else {
            delete card.activeTraits.antidote;
        }
    };

    // ── Rambo — Last Stand ───────────────────────────────────────────────────
    // Every 4th attack: +50% damage (pAtk & mAtk × 1.5) and drain 40% current stamina.
    // Boost is applied in the move hook and auto-restored at the start of the next move.
    window.abilities.move['06CC8066-C869-43CA-900E-72A141E63A61'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];

        // Step 1: Restore stats from previous 4th-attack boost
        if (card._ramboBoostActive) {
            card.pAtk = card._ramboPrevPAtk;
            card.mAtk = card._ramboPrevMAtk;
            card._ramboBoostActive = false;
        }

        // Step 2: Increment attack counter
        card._ramboAttackCount = (card._ramboAttackCount || 0) + 1;

        if (card._ramboAttackCount % 4 !== 0) {
            card.activeTraits.lastStand = { label: 'Last Stand ' + card._ramboAttackCount + '/4', turnsLeft: 9999 };
            return;
        }

        // 4th attack — save, boost, drain
        card._ramboPrevPAtk = Number(card.pAtk) || 0;
        card._ramboPrevMAtk = Number(card.mAtk) || 0;
        card.pAtk = Math.round(card._ramboPrevPAtk * 1.50);
        card.mAtk = Math.round(card._ramboPrevMAtk * 1.50);
        window.flashStatGlow(card, 'pAtk', card._ramboPrevPAtk, card.pAtk);
        window.flashStatGlow(card, 'mAtk', card._ramboPrevMAtk, card.mAtk);
        card._ramboBoostActive = true;

        var drainAmount = Math.floor((Number(card.currentStamina) || 0) * 0.40);
        card.currentStamina = Math.max(0, (Number(card.currentStamina) || 0) - drainAmount);

        card.activeTraits.lastStand = { label: 'Last Stand \u2605 +50% dmg', turnsLeft: 9999 };
        effects.add(card, ['powerup']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Last Stand: 4th attack — +50% damage, drained ' + drainAmount + ' stamina',
                'ability',
                { cardId: card.id, pAtk: card.pAtk, mAtk: card.mAtk, drainAmount: drainAmount }
            );
        }
    };

    // ── Reptile — Blood Frenzy ───────────────────────────────────────────────
    // While Reptile is bleeding, pAtk and mAtk are boosted by 20%.
    // Restored to base when bleeding expires.
    window.abilities.passive['F42F0D87-9BEA-43DB-AAF0-5CD781F5B78F'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        var isBleeding = window.traits && traits.has(card, 'bleeding');

        if (isBleeding) {
            var boostedPAtk = Math.round(card._basePAtk * 1.20);
            var boostedMAtk = Math.round(card._baseMAtk * 1.20);
            if (card.pAtk !== boostedPAtk || card.mAtk !== boostedMAtk) {
                var _origReptilePAtk = card.pAtk;
                var _origReptileMAtk = card.mAtk;
                card.pAtk = boostedPAtk;
                card.mAtk = boostedMAtk;
                window.flashStatGlow(card, 'pAtk', _origReptilePAtk, boostedPAtk);
                window.flashStatGlow(card, 'mAtk', _origReptileMAtk, boostedMAtk);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(card.name + '\'s Blood Frenzy: bleeding fuels +20% attack', 'ability', { cardId: card.id });
                }
            }
            card.activeTraits.bloodFrenzy = { label: 'Blood Frenzy +20% atk', turnsLeft: 9999 };
        } else {
            // Restore baseline when no longer bleeding
            if (card.pAtk !== card._basePAtk || card.mAtk !== card._baseMAtk) {
                card.pAtk = card._basePAtk;
                card.mAtk = card._baseMAtk;
            }
            delete card.activeTraits.bloodFrenzy;
        }
    };

    // ── Riddler — Pressure Point ─────────────────────────────────────────────
    // If the opponent lands 4 consecutive hits without missing, Riddler's next
    // attack is a guaranteed critical hit. A miss from the opponent resets the
    // streak and removes the crit guarantee.

    // damaged — opponent hit Riddler; increment streak
    window.abilities.damaged['71F2CF0D-F59F-4784-8B76-B978CF724123'] = function(team1, team2, index1, index2, _damage, _events, _isCrit, logFn) {
        var card     = team1[index1]; // Riddler
        var attacker = team2[index2];

        if (card._baseCritChance === undefined) {
            card._baseCritChance = Number(card.critChance) || 0;
        }

        // Reset counter if a new attacker appears
        if (card._riddlerLastAttackerId !== (attacker && attacker.id)) {
            card._riddlerLastAttackerId = attacker && attacker.id;
            card._riddlerConsecHits = 0;
        }

        card._riddlerConsecHits = (card._riddlerConsecHits || 0) + 1;

        if (card._riddlerConsecHits < 4) {
            card.activeTraits.pressurePoint = { label: 'Pressure ' + card._riddlerConsecHits + '/4', turnsLeft: 9999 };
            return;
        }

        // 4+ consecutive hits — guarantee crit
        if (card.critChance !== 100) {
            card.critChance = 100;
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(card.name + '\'s Pressure Point: opponent hit 4 in a row — next attack guaranteed crit', 'ability', { cardId: card.id });
            }
        }
        card.activeTraits.pressurePoint = { label: 'Pressure Point \u2605 Crit Guaranteed', turnsLeft: 9999 };
    };

    // onMissed — opponent missed Riddler; reset streak and remove guarantee
    window.abilities.onMissed['71F2CF0D-F59F-4784-8B76-B978CF724123'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        var card = team1[index1];
        card._riddlerConsecHits = 0;
        if (card._baseCritChance !== undefined) { card.critChance = card._baseCritChance; }
        delete card.activeTraits.pressurePoint;
    };

    // ── Roy Batty — Adaptive Plating ────────────────────────────────────────
    // Critical hits have a 40% chance to fully replenish armor and ward to their base values.
    window.abilities.onCrit['345986FF-FF97-4D35-9FB4-EB08A26E10B0'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];

        // Snapshot baselines once
        if (card._baseArmor === undefined) { card._baseArmor = Number(card.armor) || 0; }
        if (card._baseWard  === undefined) { card._baseWard  = Number(card.ward)  || 0; }

        if (Math.random() >= 0.40) { return; } // 60% — no replenish

        card.armor = card._baseArmor;
        card.ward  = card._baseWard;

        effects.add(card, ['glow-green']);
        card.activeTraits.adaptivePlating = { label: 'Adaptive Plating: Armor/Ward Restored', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Adaptive Plating: critical hit restored armor and ward to base',
                'ability',
                { cardId: card.id, armor: card.armor, ward: card.ward }
            );
        }
    };

    // ── Kratos — Warlord's Momentum ──────────────────────────────────────────
    // After 6 total attacks, physical damage is increased by 50%.
    // Counter tracks all move executions; boost activates at 6 and stays active.
    window.abilities.move['3F755BF1-ED38-4908-8E5C-50208C203D0C'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];

        // Initialise baseline pAtk once
        if (card._basePAtk === undefined) {
            card._basePAtk = Number(card.pAtk) || 0;
        }

        // Increment attack counter
        card._kratosAttackCount = (card._kratosAttackCount || 0) + 1;

        if (card._kratosAttackCount < 6) {
            // Update "charging" badge so the player can see progress
            card.activeTraits.warlordMomentum = {
                label: 'Momentum ' + card._kratosAttackCount + '/6',
                turnsLeft: 9999
            };
            return;
        }

        // 6th attack or beyond — apply / maintain 50% boost
        var boostedPAtk = Math.round(card._basePAtk * 1.50);
        if (card.pAtk !== boostedPAtk) {
            var _origKratosPAtk = card.pAtk;
            card.pAtk = boostedPAtk;
            window.flashStatGlow(card, 'pAtk', _origKratosPAtk, boostedPAtk);
            effects.add(card, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Warlord\'s Momentum: physical attack increased by 50%',
                    'ability',
                    { cardId: card.id, pAtk: card.pAtk }
                );
            }
        }
        card.activeTraits.warlordMomentum = {
            label: 'Momentum MAXED \u2605 +50% pAtk',
            turnsLeft: 9999
        };
    };

    // ─── Samus Aran — Ward Bypass ───────────────────────────────────────────────
    // Card ID  : A5C1EAC8-76D7-47E9-AACF-EEFD18BFD121
    // Ability  : 15% chance that opponent's ward doesn't protect against critical hits
    // Ability ID: 76CA2947-0222-46B1-B96A-4D98A407EAB4
    //
    // Mechanics:
    //   move  : 15% pre-roll sets _samusWardBypass = true for this attack
    //   onCrit: if _samusWardBypass, deal extra damage equal to opponent.ward × crit multiplier
    //           (compensates for ward already being subtracted before the crit multiplier was applied)
    // ─────────────────────────────────────────────────────────────────────────────

    // ─── Scorpion — Infernal Resolve ─────────────────────────────────────────────
    // Card ID  : 862A8BAE-ED5B-4BA5-8EF6-4B478D427986
    // Ability  : Burning is ineffective and increases physical attack by 20%
    // Ability ID: 82FAD35B-1121-41C4-9E00-C78027289A90
    //
    // Mechanics:
    //   passive : stamps _burningImmune = true so traits.js skips burning damage
    //             if burning is active → pAtk = basePAtk × 1.20
    //             if not burning       → restore pAtk to basePAtk
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.passive['82FAD35B-1121-41C4-9E00-C78027289A90'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._basePAtk === undefined) {
            card._basePAtk = Number(card.pAtk) || 0;
        }

        // Always stamp immunity so traits.js burning tick never deals damage
        card._burningImmune = true;

        var isBurning = window.traits && traits.has(card, 'burning');

        if (isBurning) {
            var boosted = Math.round(card._basePAtk * 1.20);
            if (card.pAtk !== boosted) {
                var _origScorpionPAtk = card.pAtk;
                card.pAtk = boosted;
                window.flashStatGlow(card, 'pAtk', _origScorpionPAtk, boosted);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Infernal Resolve: burning fuels attack — pAtk +20%',
                        'ability',
                        { cardId: card.id, pAtk: card.pAtk }
                    );
                }
            }
            card.activeTraits.infernalResolve = { label: 'Infernal Resolve +20% pAtk', turnsLeft: 9999 };
        } else {
            if (card.pAtk !== card._basePAtk) {
                card.pAtk = card._basePAtk;
            }
            delete card.activeTraits.infernalResolve;
        }
    };

    // ─── Scarecrow — Manic Focus ─────────────────────────────────────────────────
    // Card ID  : 16E49FE3-6089-47DD-A8D4-B4F1263DCC8F
    // Ability  : When confused, critical hit chance increased by 20%
    // Ability ID: AAB74A33-2593-4C71-BD3D-0AF2FA25420B
    //
    // Mechanics:
    //   passive : checks own confused status each turn
    //             if confused  → critChance = baseCritChance + 20
    //             if not       → restore critChance to baseCritChance
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.passive['AAB74A33-2593-4C71-BD3D-0AF2FA25420B'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._baseCritChance === undefined) {
            card._baseCritChance = Number(card.critChance) || 0;
        }

        var isConfused = window.traits && traits.has(card, 'confused');

        if (isConfused) {
            var boosted = card._baseCritChance + 20;
            if (card.critChance !== boosted) {
                var _origScarecrowCrit = card.critChance;
                card.critChance = boosted;
                window.flashStatGlow(card, 'critChance', _origScarecrowCrit, boosted);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Manic Focus: confusion sharpens aim — crit chance +20%',
                        'ability',
                        { cardId: card.id, critChance: card.critChance }
                    );
                }
            }
            card.activeTraits.manicFocus = { label: 'Manic Focus +20% Crit', turnsLeft: 9999 };
        } else {
            if (card.critChance !== card._baseCritChance) {
                card.critChance = card._baseCritChance;
            }
            delete card.activeTraits.manicFocus;
        }
    };

    // ─── Samus Aran — Ward Bypass ───────────────────────────────────────────────
    window.abilities.move['76CA2947-0222-46B1-B96A-4D98A407EAB4'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        var card = team1[index1];
        card._samusWardBypass = Math.random() < 0.15;
        if (card._samusWardBypass) {
            card.activeTraits.wardBypass = { label: 'Ward Bypass Ready', turnsLeft: 9999 };
        } else {
            delete card.activeTraits.wardBypass;
        }
    };

    window.abilities.onCrit['76CA2947-0222-46B1-B96A-4D98A407EAB4'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        if (!card._samusWardBypass) { return; }
        card._samusWardBypass = false;

        if (!opponent || opponent.hp <= 0) { return; }

        var ward = Number(opponent.ward) || 0;
        if (ward <= 0) { return; }

        // Ward was already subtracted from mAtk before the crit multiplier — to simulate
        // "ward provides no protection", we deal the ward value as if it had been included
        // in the base damage that was then multiplied by the crit multiplier.
        var critMult = (Number(card.critDamage) || 150) / 100;
        var bonus    = Math.round(ward * critMult);

        opponent.hp = Math.max(0, opponent.hp - bonus);
        effects.add(card,    ['powerup']);
        effects.add(opponent, ['shakeY']);
        card.activeTraits.wardBypass = { label: 'Ward Bypass \u2605 Triggered!', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Ward Bypass: dealt ' + bonus + ' extra damage (ward ignored on crit)',
                'ability',
                { cardId: card.id, bonus: bonus, ward: ward }
            );
        }
    };

    // ─── Shao Kahn — Kahn's Retribution ──────────────────────────────────────────
    // Card ID  : EEC546B7-81D7-4B68-9216-2A8C2D006F1E
    // Ability  : -500 HP to enemy when there is a trade of critical hits
    // Ability ID: 39A7A501-BBC5-44AE-BD9C-88B8254E40DA
    //
    // Mechanics (bidirectional — covers both attack-order scenarios):
    //   move             : reset _shaoJustCritted so it only covers the current attack window
    //   onCrit           : set _shaoJustCritted; if _shaoWasCritted also set → trade detected
    //   damaged (isCrit) : set _shaoWasCritted; if _shaoJustCritted also set → trade detected
    //
    //   _shaoJustCritted : Shao Kahn critted this attack (cleared each move)
    //   _shaoWasCritted  : opponent critted Shao Kahn (persists until trade fires)
    // ─────────────────────────────────────────────────────────────────────────────

    function _shaoFireRetribution(card, opponent, logFn) {
        if (!opponent || opponent.hp <= 0) { return; }
        opponent.hp = Math.max(0, opponent.hp - 500);
        effects.add(card,    ['powerup']);
        effects.add(opponent, ['shakeY']);
        card.activeTraits.kahnRetribution = { label: 'Kahn\'s Retribution \u2605 -500 HP', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Kahn\'s Retribution: critical hit trade — enemy takes 500 bonus damage',
                'ability',
                { cardId: card.id, bonus: 500 }
            );
        }
    }

    window.abilities.move['39A7A501-BBC5-44AE-BD9C-88B8254E40DA'] = function(team1, _team2, index1, _index2, _events, _logFn) {
        team1[index1]._shaoJustCritted = false;
    };

    window.abilities.onCrit['39A7A501-BBC5-44AE-BD9C-88B8254E40DA'] = function(team1, team2, index1, index2, _events, logFn) {
        var card = team1[index1];
        card._shaoJustCritted = true;
        card.activeTraits.kahnRetribution = { label: 'Kahn\'s Retribution: Crit Ready', turnsLeft: 9999 };
        if (card._shaoWasCritted) {
            // Opponent critted first, Shao Kahn crits back → trade
            card._shaoWasCritted = false;
            card._shaoJustCritted = false;
            _shaoFireRetribution(card, team2[index2], logFn);
        }
    };

    window.abilities.damaged['39A7A501-BBC5-44AE-BD9C-88B8254E40DA'] = function(team1, team2, index1, index2, _damage, _events, isCrit, logFn) {
        var card = team1[index1];
        if (!isCrit) { return; }
        card._shaoWasCritted = true;
        if (card._shaoJustCritted) {
            // Shao Kahn critted first, opponent crits back → trade
            card._shaoWasCritted = false;
            card._shaoJustCritted = false;
            _shaoFireRetribution(card, team2[index2], logFn);
        } else {
            card.activeTraits.kahnRetribution = { label: 'Kahn\'s Retribution: Critted!', turnsLeft: 9999 };
        }
    };

    // ─── Stark — Toxin Mastery ────────────────────────────────────────────────────
    // Card ID  : D8A86587-22B7-4700-95FA-2CFDB3578D66
    // Ability  : Immune to Poison. HP drain reversed onto opponent. While poisoned: pAtk/mAtk +400, HP +400
    // Ability ID: CA568ACE-664C-4B46-A9FE-4C98EBC68D0E
    //
    // Mechanics:
    //   passive : stamps _poisonImmune + stores _starkPoisonReverseTarget for traits.js end-of-turn
    //             if poisoned → pAtk/mAtk += 400 from base; one-time +400 HP heal on activation
    //             if not      → restore pAtk/mAtk to base
    //   traits.js processEndOfTurn: _poisonImmune branch deals 450 to _starkPoisonReverseTarget
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.passive['CA568ACE-664C-4B46-A9FE-4C98EBC68D0E'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        // Always stamp immunity and store opponent ref for end-of-turn reversal
        card._poisonImmune           = true;
        card._starkPoisonReverseTarget = opposingTeam[opposingIndex] || null;

        var isPoisoned = window.traits && traits.has(card, 'poisoned');

        if (isPoisoned) {
            var boostedPAtk = card._basePAtk + 400;
            var boostedMAtk = card._baseMAtk + 400;

            if (card.pAtk !== boostedPAtk || card.mAtk !== boostedMAtk) {
                var _origStarkPAtk = card.pAtk;
                var _origStarkMAtk = card.mAtk;
                card.pAtk = boostedPAtk;
                card.mAtk = boostedMAtk;
                window.flashStatGlow(card, 'pAtk', _origStarkPAtk, boostedPAtk);
                window.flashStatGlow(card, 'mAtk', _origStarkMAtk, boostedMAtk);
                effects.add(card, ['powerup']);
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Toxin Mastery: poison absorbed — pAtk/mAtk +400',
                        'ability',
                        { cardId: card.id, pAtk: card.pAtk, mAtk: card.mAtk }
                    );
                }
            }

            // One-time +400 HP heal on activation
            if (!card._starkPoisonHpBoosted) {
                card._starkPoisonHpBoosted = true;
                var maxHp = card.originalHp || card.hp;
                card.hp = Math.min(maxHp, card.hp + 400);
                effects.add(card, ['glow-green']);
            }

            card.activeTraits.toxinMastery = { label: 'Toxin Mastery: Poison \u2192 Power', turnsLeft: 9999 };
        } else {
            card._starkPoisonHpBoosted = false;
            if (card.pAtk !== card._basePAtk || card.mAtk !== card._baseMAtk) {
                card.pAtk = card._basePAtk;
                card.mAtk = card._baseMAtk;
            }
            delete card.activeTraits.toxinMastery;
        }
    };

    // ─── Syler — Mind Steal ───────────────────────────────────────────────────────
    // Card ID  : 9A8AFB5F-AEA2-4B41-9D19-748FED92CCEA
    // Ability  : Being confused adds 40 ward. If opponent is confused, crits have 70% chance to steal ability.
    // Ability ID: 797347F0-AE47-4EBF-B092-D6C28A1D1010
    //
    // Mechanics:
    //   passive : if Syler has 'confused' → ward = baseWard + 40; else restore
    //             (only runs while Syler still holds his original abilityKey)
    //   onCrit  : if opponent has 'confused' → 70% roll →
    //             card.abilityKey  = opponent.abilityKey   (Syler now dispatches stolen hooks)
    //             card.ability     = opponent.ability      (UI shows stolen ability name)
    //             opponent.abilityKey = null               (opponent disabled)
    //             opponent.ability    = 'Ability Stolen'
    //             battleArena.js reads abilityKey fresh every dispatch — stolen hooks
    //             fire for Syler from the very next turn.
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.passive['797347F0-AE47-4EBF-B092-D6C28A1D1010'] = function(card, _cardIndex, _team, _opposingTeam, _opposingIndex, _events, logFn) {
        if (card._baseWard === undefined) { card._baseWard = Number(card.ward) || 0; }
        var isConfused = window.traits && traits.has(card, 'confused');
        if (isConfused) {
            var boosted = card._baseWard + 40;
            if (card.ward !== boosted) {
                var _origSylerWard = card.ward;
                card.ward = boosted;
                window.flashStatGlow(card, 'ward', _origSylerWard, boosted);
                effects.add(card, ['glow-blue']);
                if (typeof logFn === 'function') {
                    logFn(card.name + '\'s Mind Steal: confusion sharpens defences — ward +40', 'ability', { cardId: card.id, ward: card.ward });
                }
            }
            card.activeTraits.mindStealWard = { label: 'Confused +40 Ward', turnsLeft: 9999 };
        } else {
            if (card.ward !== card._baseWard) { card.ward = card._baseWard; }
            delete card.activeTraits.mindStealWard;
        }
    };

    window.abilities.onCrit['797347F0-AE47-4EBF-B092-D6C28A1D1010'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        if (!opponent || opponent.hp <= 0) { return; }
        if (!window.traits || !traits.has(opponent, 'confused')) { return; }
        if (Math.random() >= 0.70) { return; }
        if (!opponent.abilityKey) { return; }

        // Directly replace Syler's ability with the stolen one
        card._sylerOriginalKey    = card.abilityKey;
        card._sylerOriginalAbility = card.ability;
        card.abilityKey  = opponent.abilityKey;
        card.ability     = opponent.ability;

        // Disable opponent's ability
        opponent.abilityKey = null;
        opponent.ability    = 'Ability Stolen';

        effects.add(card,    ['powerup']);
        effects.add(opponent, ['shakeY']);
        card.activeTraits.mindSteal     = { label: 'Mind Steal \u2605 Now using: ' + card.ability, turnsLeft: 9999 };
        opponent.activeTraits.mindSteal = { label: 'Ability Stolen', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Mind Steal: stole ' + card.ability + ' from confused ' + opponent.name + ' — now using their ability!',
                'ability',
                { cardId: card.id, stolenAbility: card.ability }
            );
        }
    };

    // ─── Stellan — Surge Protocol ────────────────────────────────────────────────
    // Card ID  : E331A734-1426-4296-A388-4BF0FA818B7F
    // Ability  : 10% of attacks get stored into unleashed critical hits
    // Ability ID: AF8588E8-A9AE-4DF8-AEC8-0CC0D17198AF
    //
    // Mechanics:
    //   move   : 10% chance to store a charge (_stellanStoredCount++)
    //   onCrit : if charges stored, unleash all as bonus damage
    //            damage per charge = mAtk × (critDamage / 100); charges reset to 0
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.move['AF8588E8-A9AE-4DF8-AEC8-0CC0D17198AF'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        if (Math.random() < 0.99) {
            card._stellanStoredCount = (card._stellanStoredCount || 0) + 1;
            card.activeTraits.surgeProtocol = { label: 'Surge Protocol: ' + card._stellanStoredCount + ' stored', turnsLeft: 9999 };
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Surge Protocol: attack stored (' + card._stellanStoredCount + ' total)',
                    'ability',
                    { cardId: card.id, stored: card._stellanStoredCount }
                );
            }
        }
    };

    window.abilities.onCrit['AF8588E8-A9AE-4DF8-AEC8-0CC0D17198AF'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        if (!card._stellanStoredCount || card._stellanStoredCount <= 0) { return; }
        if (!opponent || opponent.hp <= 0) { return; }

        var stored       = card._stellanStoredCount;
        card._stellanStoredCount = 0;

        var critMult     = (Number(card.critDamage) || 150) / 100;
        var dmgPerCharge = Math.round((Number(card.mAtk) || 0) * critMult);
        var totalDamage  = dmgPerCharge * stored;

        opponent.hp = Math.max(0, opponent.hp - totalDamage);
        effects.add(card,    ['powerup']);
        effects.add(opponent, ['shakeY']);
        card.activeTraits.surgeProtocol = { label: 'Surge Protocol \u2605 ' + stored + '\u00d7 Crit Unleashed!', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Surge Protocol: unleashed ' + stored + ' stored crit(s) — ' + totalDamage + ' bonus damage!',
                'ability',
                { cardId: card.id, stored: stored, totalDamage: totalDamage }
            );
        }
    };

    // ─── Sonya — Status Reflect ───────────────────────────────────────────────────
    // Card ID  : 46F47497-BB88-4094-8E53-ABE1ABE46A53
    // Ability  : 25% chance to reverse a negative status effect onto the opponent
    // Ability ID: 9E237BC7-8ED6-413F-A105-A58634762333
    //
    // Mechanics:
    //   passive : each tick, iterate active debuffs on Sonya
    //             each debuff gets ONE roll (tracked via _sonyaReflectOffered map)
    //             25% hit → traits.remove from Sonya, traits.apply to opponent
    //             75% miss → debuff stays; no re-roll on future ticks
    //             when debuff naturally expires, clear its offered flag (ready for re-application)
    // ─────────────────────────────────────────────────────────────────────────────

    var _sonyaDebuffs = [
        'slowed', 'staggered', 'pacified', 'shocked', 'withered', 'broken',
        'dominated', 'disarmed', 'hallucinating', 'bleeding', 'frozen',
        'burning', 'poisoned', 'stunned', 'weakened', 'blinded', 'confused'
    ];

    window.abilities.passive['9E237BC7-8ED6-413F-A105-A58634762333'] = function(card, _cardIndex, _team, opposingTeam, opposingIndex, _events, logFn) {
        var opponent = opposingTeam[opposingIndex];
        if (!opponent || opponent.hp <= 0) { return; }

        if (!card._sonyaReflectOffered) { card._sonyaReflectOffered = {}; }

        for (var i = 0; i < _sonyaDebuffs.length; i++) {
            var t = _sonyaDebuffs[i];

            if (window.traits && traits.has(card, t)) {
                if (!card._sonyaReflectOffered[t]) {
                    // First time seeing this debuff — roll once
                    card._sonyaReflectOffered[t] = true;

                    if (Math.random() < 0.99) {
                        traits.remove(card, t);
                        traits.apply(opponent, t);
                        effects.add(card,    ['powerup']);
                        effects.add(opponent, ['shakeY']);
                        card.activeTraits.statusReflect = { label: 'Status Reflect \u2605 ' + t + ' reversed!', turnsLeft: 9999 };
                        if (typeof logFn === 'function') {
                            logFn(
                                card.name + '\'s Status Reflect: ' + t + ' reversed onto ' + opponent.name + '!',
                                'ability',
                                { cardId: card.id, trait: t }
                            );
                        }
                    }
                }
            } else {
                // Debuff expired or was never present — clear offered flag for future re-application
                delete card._sonyaReflectOffered[t];
            }
        }
    };

    // ─── Shredder — Death Spiral ──────────────────────────────────────────────────
    // Card ID  : 34D876AC-1FB8-44BE-83FC-70E13203A3D6
    // Ability  : Opponent card loses 20% speed each time after landing a critical hit
    // Ability ID: ED7DE0DA-4DCD-48E6-B770-01946E0D57A6
    //
    // Mechanics:
    //   onCrit : each crit applies ×0.80 to opponent's current speed (stacking, permanent per card)
    //            tracks opponent ID — resets stacks when a new opponent card appears
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.onCrit['ED7DE0DA-4DCD-48E6-B770-01946E0D57A6'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        if (!opponent || opponent.hp <= 0) { return; }

        // Reset stack count when facing a new opponent card
        if (card._shredderLastOpponentId !== opponent.id) {
            card._shredderLastOpponentId = opponent.id;
            card._shredderSpeedStacks    = 0;
        }

        card._shredderSpeedStacks = (card._shredderSpeedStacks || 0) + 1;

        // Snapshot opponent base speed once per opponent card
        if (opponent._shredderBaseSpeed === undefined) {
            opponent._shredderBaseSpeed = Number(opponent.speed) || 0;
        }

        // Each stack applies ×0.80 compounding from the original base
        opponent.speed = Math.round(opponent._shredderBaseSpeed * Math.pow(0.80, card._shredderSpeedStacks));

        var pctLost = Math.round((1 - Math.pow(0.80, card._shredderSpeedStacks)) * 100);
        opponent.activeTraits.deathSpiral = { label: 'Death Spiral \u2212' + pctLost + '% Speed', turnsLeft: 9999 };
        effects.add(card,    ['powerup']);
        effects.add(opponent, ['shakeY']);

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Death Spiral: ' + opponent.name + ' speed reduced by ' + pctLost + '% (stack ' + card._shredderSpeedStacks + ')',
                'ability',
                { cardId: card.id, targetId: opponent.id, speed: opponent.speed, stacks: card._shredderSpeedStacks }
            );
        }
    };

    // ─── Thalana — Cleansing Strike ───────────────────────────────────────────────
    // Card ID  : 11B6118D-5B87-4FD8-B40D-1527A65C7616
    // Ability  : Critical hit cures 1 negative status effect
    // Ability ID: 77879EF3-029A-48B5-A058-BFC0168D94C4
    //
    // Mechanics:
    //   onCrit : iterate debuffs on Thalana; remove the first active one via traits.remove
    // ─────────────────────────────────────────────────────────────────────────────

    var _thalanaDebuffs = [
        'slowed', 'staggered', 'pacified', 'shocked', 'withered', 'broken',
        'dominated', 'disarmed', 'hallucinating', 'bleeding', 'frozen',
        'burning', 'poisoned', 'stunned', 'weakened', 'blinded', 'confused'
    ];

    window.abilities.onCrit['77879EF3-029A-48B5-A058-BFC0168D94C4'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];

        for (var i = 0; i < _thalanaDebuffs.length; i++) {
            var t = _thalanaDebuffs[i];
            if (window.traits && traits.has(card, t)) {
                traits.remove(card, t);
                effects.add(card, ['glow-green']);
                card.activeTraits.cleansingStrike = { label: 'Cleansing Strike: ' + t + ' cured!', turnsLeft: 9999 };
                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Cleansing Strike: critical hit cured ' + t + '!',
                        'ability',
                        { cardId: card.id, cured: t }
                    );
                }
                return; // one debuff per crit
            }
        }
    };

    // ─── Thor — Stamina Sap ───────────────────────────────────────────────────────
    // Card ID  : 55495F8C-674F-45A0-99F9-5E4F22EE3F7F
    // Ability  : Missed attacks from opponent drain stamina 50% more
    // Ability ID: 1CEDD6C4-DC9D-4BE8-A699-23C0ED49DDE6
    //
    // Mechanics:
    //   onMissed : attacker already paid their normal staminaCost before the miss
    //              recalculate staminaCost from attacker.pAtk (same formula as battleArena.js)
    //              drain an extra 50% of that cost from the attacker's currentStamina
    // ─────────────────────────────────────────────────────────────────────────────

    window.abilities.onMissed['1CEDD6C4-DC9D-4BE8-A699-23C0ED49DDE6'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1]; // Thor — the defender who was missed
        var attacker = team2[index2]; // the attacker who missed

        if (!attacker || attacker.hp <= 0) { return; }

        var currentStamina = Number(attacker.currentStamina) || 0;
        if (currentStamina <= 0) { return; }

        if (attacker._throttleStaminaImmune) {
            attacker._throttleStaminaBlockedThisTick = true;
            effects.add(card,    ['shakeY']);
            effects.add(attacker, ['powerup']);
            if (typeof logFn === 'function') {
                logFn(attacker.name + '\'s Overdrive: Thor\'s Stamina Sap blocked!', 'ability', { cardId: attacker.id });
            }
            return;
        }

        var extraDrain  = Math.floor(currentStamina * 0.50);
        attacker.currentStamina = Math.max(0, currentStamina - extraDrain);

        effects.add(card,    ['powerup']);
        effects.add(attacker, ['shakeY']);
        card.activeTraits.staminaSap = { label: 'Stamina Sap: Miss \u2212' + extraDrain + ' stamina', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Stamina Sap: ' + attacker.name + ' missed — drained ' + extraDrain + ' extra stamina!',
                'ability',
                { cardId: card.id, targetId: attacker.id, extraDrain: extraDrain }
            );
        }
    };

    // ─── Throttle — Overdrive ────────────────────────────────────────────────────
    // passive: stamp _throttleSlowImmune + _throttleStaminaImmune each tick;
    //          slowed active → +500 pAtk per speed-nerfing effect (flat additive);
    //          stamina drains blocked externally → +500 mAtk per block stack (permanent).

    window.abilities.passive['42928B06-6BE2-4629-90EA-70108EDAAA25'] = function(card, _myTeam, _myIndex, _oppTeam, _oppIndex, _events, _logFn) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }
        if (card._baseMAtk === undefined) { card._baseMAtk = Number(card.mAtk) || 0; }

        // Always stamp immunity flags
        card._throttleSlowImmune    = true;
        card._throttleStaminaImmune = true;

        // ── Speed nerf immunity → +500 pAtk per turn slowed is active (resets when slowed expires) ──
        var isSlowed = window.traits && traits.has(card, 'slowed');
        if (isSlowed) {
            card._throttleSlowTurnStacks = (card._throttleSlowTurnStacks || 0) + 1;
        } else {
            card._throttleSlowTurnStacks = 0;
        }
        var newPAtk = card._basePAtk + (card._throttleSlowTurnStacks * 500);
        if (card.pAtk !== newPAtk) {
            var _origThrottlePAtk = card.pAtk;
            card.pAtk = newPAtk;
            if (isSlowed && newPAtk > _origThrottlePAtk) { window.flashStatGlow(card, 'pAtk', _origThrottlePAtk, newPAtk); }
            if (isSlowed) { effects.add(card, ['powerup']); }
        }
        if (card._throttleSlowTurnStacks > 0) {
            card.activeTraits.overdrivePAtk = { label: 'Overdrive: Slow \u2192 +' + (card._throttleSlowTurnStacks * 500) + ' pAtk (' + card._throttleSlowTurnStacks + ' turns)', turnsLeft: 9999 };
        } else {
            delete card.activeTraits.overdrivePAtk;
        }

        // ── Stamina nerf immunity → +500 mAtk per turn a drain was blocked (resets when no drain this tick) ──
        if (card._throttleStaminaBlockedThisTick) {
            card._throttleStaminaTurnStacks = (card._throttleStaminaTurnStacks || 0) + 1;
            card._throttleStaminaBlockedThisTick = false;
        } else {
            card._throttleStaminaTurnStacks = 0;
        }
        var newMAtk = card._baseMAtk + (card._throttleStaminaTurnStacks * 500);
        if (card.mAtk !== newMAtk) {
            var _origThrottleMAtk = card.mAtk;
            card.mAtk = newMAtk;
            if (card._throttleStaminaTurnStacks > 0 && newMAtk > _origThrottleMAtk) { window.flashStatGlow(card, 'mAtk', _origThrottleMAtk, newMAtk); }
            if (card._throttleStaminaTurnStacks > 0) { effects.add(card, ['powerup']); }
        }
        if (card._throttleStaminaTurnStacks > 0) {
            card.activeTraits.overdriveMAtk = { label: 'Overdrive: Drain Blocked \u2192 +' + (card._throttleStaminaTurnStacks * 500) + ' mAtk (' + card._throttleStaminaTurnStacks + ' turns)', turnsLeft: 9999 };
        } else {
            delete card.activeTraits.overdriveMAtk;
        }
    };

    // ─── Thraxel — Relentless Assault ────────────────────────────────────────────
    // move   : restore ward after 1-turn strip; reset streak if last attack missed
    // onHit  : track consecutive hits; at 3 → −50% ward (permanent);
    //          at 4+ → ward = 0 for 1 turn then restore to halved value

    window.abilities.move['F0C5BD96-0DBA-4FB5-BE18-D35B278F569E'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];

        // Restore ward after 1-turn full strip
        if (card._thraxelWardStripped && opponent) {
            opponent.ward = card._thraxelWardRestoreValue || 0;
            delete opponent.activeTraits.wardStripped;
            card._thraxelWardStripped = false;
            if (typeof logFn === 'function') {
                logFn(
                    opponent.name + '\'s ward restored to ' + opponent.ward + ' after Thraxel\'s strip wore off.',
                    'ability',
                    { cardId: card.id }
                );
            }
        }

        // If last attack missed (onHit never fired), reset streak
        if (!card._thraxelHitThisTurn && (card._thraxelConsecHits || 0) > 0) {
            card._thraxelConsecHits = 0;
            delete card.activeTraits.thraxelStreak;
        }
        card._thraxelHitThisTurn = false;
    };

    window.abilities.onHit['F0C5BD96-0DBA-4FB5-BE18-D35B278F569E'] = function(team1, team2, index1, index2, _events, logFn) {
        var card     = team1[index1];
        var opponent = team2[index2];
        if (!opponent || opponent.hp <= 0) { return; }

        card._thraxelHitThisTurn  = true;
        card._thraxelConsecHits   = (card._thraxelConsecHits || 0) + 1;
        var hits = card._thraxelConsecHits;

        // Snapshot opponent's original ward on first hit
        if (opponent._thraxelBaseWard === undefined) {
            opponent._thraxelBaseWard = Number(opponent.ward) || 0;
        }

        if (hits >= 4) {
            // Remove ward entirely for 1 turn, restore to −50% value next move
            var halfWard = Math.floor(opponent._thraxelBaseWard * 0.50);
            card._thraxelWardRestoreValue = halfWard;
            card._thraxelWardStripped     = true;
            opponent.ward = 0;
            opponent.activeTraits.wardStripped  = { label: 'Ward Stripped! (1 turn)', turnsLeft: 9999 };
            card.activeTraits.thraxelStreak     = { label: 'Relentless Assault \u2605 ' + hits + ' hits \u2192 Ward = 0!', turnsLeft: 9999 };
            effects.add(card,    ['powerup']);
            effects.add(opponent, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Relentless Assault: ' + hits + ' consecutive hits — ' + opponent.name + '\'s ward stripped to 0 for 1 turn!',
                    'ability',
                    { cardId: card.id, hits: hits, ward: 0 }
                );
            }
        } else if (hits === 3) {
            // Reduce ward by 50% permanently
            var reducedWard = Math.floor(opponent._thraxelBaseWard * 0.50);
            opponent.ward = reducedWard;
            opponent.activeTraits.wardReduced   = { label: 'Ward \u221250% (Relentless Assault)', turnsLeft: 9999 };
            card.activeTraits.thraxelStreak     = { label: 'Relentless Assault: Ward \u221250% (' + hits + ' hits)', turnsLeft: 9999 };
            effects.add(card,    ['powerup']);
            effects.add(opponent, ['shakeY']);
            if (typeof logFn === 'function') {
                logFn(
                    card.name + '\'s Relentless Assault: 3 consecutive hits — ' + opponent.name + '\'s ward reduced by 50% (' + reducedWard + ')!',
                    'ability',
                    { cardId: card.id, hits: hits, reducedWard: reducedWard }
                );
            }
        } else {
            card.activeTraits.thraxelStreak = { label: 'Relentless Assault: ' + hits + '/4 hits', turnsLeft: 9999 };
        }
    };

    // ─── Titan — Shared Burden ───────────────────────────────────────────────────
    // damaged: 30% chance to intercept damage. Undo the full hit to Titan, reduce
    //          total by 10%, then divide equally across all living team members.

    window.abilities.damaged['97F8CC5E-B30E-4C51-8FFC-6DEDD7B402BE'] = function(team1, _team2, index1, _index2, damage, _events, _isCrit, logFn) {
        if (Math.random() >= 0.99) { return; }
        if (!damage || damage <= 0) { return; }

        var card = team1[index1];

        // Restore the full damage that was already deducted from Titan
        card.hp = card.hp + damage; // no cap needed — this reverses the deduction exactly

        // Reduce total damage by 10%
        var reducedDamage = Math.round(damage * 0.90);

        // Collect all living team members (Titan included)
        var living = [];
        for (var i = 0; i < team1.length; i++) {
            if (team1[i] && team1[i].hp > 0) { living.push(team1[i]); }
        }
        if (living.length === 0) { return; }

        var splitDamage = Math.round(reducedDamage / living.length);
        if (splitDamage <= 0) { return; }

        for (var j = 0; j < living.length; j++) {
            living[j].hp = Math.max(0, living[j].hp - splitDamage);
            effects.add(living[j], ['shakeY']);
        }

        card.activeTraits.sharedBurden = { label: 'Shared Burden \u2605 \u00f7' + living.length + ' (' + splitDamage + ' each)', turnsLeft: 9999 };

        if (typeof logFn === 'function') {
            logFn(
                card.name + '\'s Shared Burden: ' + damage + ' damage split ' + living.length + '-way (' + splitDamage + ' each, \u221210%)!',
                'ability',
                { cardId: card.id, originalDamage: damage, reducedDamage: reducedDamage, splitDamage: splitDamage, targets: living.length }
            );
        }
    };

    // ─── Tsung — Soul Theft ──────────────────────────────────────────────────────
    // Delegation pattern: Tsung's abilityKey stays as D191BE5A permanently.
    // Stolen key is stored in card._tsungStolenKey. Every hook type checks for
    // a stored key and forwards the call to the stolen ability's hook.
    //
    // move  : attempt steal (20% / turn) if not yet stolen; otherwise delegate
    // passive, onHit, onCrit, damaged, onMissed, onKill : all delegate to stolen

    var _tsungKey = 'D191BE5A-0B8E-401D-95D9-F524288D77CF';

    // Forward any hook type that uses (team1, team2, index1, ...) signature
    function _tsungDelegate(hookType, args) {
        var card = args[0][args[2]]; // team1[index1]
        if (!card || !card._tsungStolenKey) { return; }
        var fn = window.abilities && abilities[hookType] && abilities[hookType][card._tsungStolenKey];
        if (typeof fn === 'function') { fn.apply(null, args); }
    }

    window.abilities.move[_tsungKey] = function(team1, team2, index1, index2, events, logFn) {
        var card = team1[index1];

        // Always roll — Tsung can steal (or re-steal) any turn
        if (Math.random() < 0.20) {
            var candidates = [];
            for (var i = 0; i < team2.length; i++) {
                if (team2[i] && team2[i].hp > 0 && team2[i].abilityKey) {
                    candidates.push(team2[i]);
                }
            }

            if (candidates.length > 0) {
                var target    = candidates[Math.floor(Math.random() * candidates.length)];
                var stolenKey  = target.abilityKey;
                var stolenName = target.ability || 'Unknown Ability';

                card._tsungStolenKey  = stolenKey;
                card._tsungStolenName = stolenName;
                target.abilityKey     = null;
                target.ability        = 'Ability Stolen';

                effects.add(card,   ['powerup']);
                effects.add(target, ['shakeY']);

                card.activeTraits.soulTheft   = { label: 'Soul Theft \u2605 Now using: ' + stolenName, turnsLeft: 9999 };
                target.activeTraits.soulTheft = { label: 'Ability Stolen by ' + card.name, turnsLeft: 9999 };

                if (typeof logFn === 'function') {
                    logFn(
                        card.name + '\'s Soul Theft: stole ' + stolenName + ' from ' + target.name + '!',
                        'ability',
                        { cardId: card.id, targetId: target.id, stolenAbility: stolenName }
                    );
                }

                // Immediately invoke stolen passive so state is ready this turn
                var passiveFn = window.abilities && abilities.passive && abilities.passive[stolenKey];
                if (typeof passiveFn === 'function') {
                    try { passiveFn.apply(null, [card, index1, team1, team2, index2, events, logFn]); } catch(e) {}
                }
            }
        }

        // Always delegate to current stolen move (whether we just stole or not)
        if (card._tsungStolenKey) {
            var moveFn = window.abilities && abilities.move && abilities.move[card._tsungStolenKey];
            if (typeof moveFn === 'function') {
                try { moveFn.apply(null, arguments); } catch(e) {}
            }
        }
    };

    // Passive — uses (card, index, team, opposingTeam, opposingIndex, events, logFn)
    window.abilities.passive[_tsungKey] = function() {
        var card = arguments[0];
        if (!card || !card._tsungStolenKey) { return; }
        var fn = window.abilities && abilities.passive && abilities.passive[card._tsungStolenKey];
        if (typeof fn === 'function') { fn.apply(null, arguments); }
    };

    // All remaining hooks use (team1, team2, index1, ...) — delegate via helper
    window.abilities.onHit[_tsungKey]     = function() { _tsungDelegate('onHit',     arguments); };
    window.abilities.onCrit[_tsungKey]    = function() { _tsungDelegate('onCrit',    arguments); };
    window.abilities.damaged[_tsungKey]   = function() { _tsungDelegate('damaged',   arguments); };
    window.abilities.onMissed[_tsungKey]  = function() { _tsungDelegate('onMissed',  arguments); };
    window.abilities.onKill[_tsungKey]    = function() { _tsungDelegate('onKill',    arguments); };

    // ─── V — Premonition ─────────────────────────────────────────────────────────
    // onMissed : fires when an opponent's attack misses V (V is the defender).
    //            If this is the opponent's first attack, snapshot and lock
    //            V's accuracy and speed for the rest of the battle.
    // damaged  : fires when V takes damage. Marks the first attack as seen
    //            (a hit — no lock triggers).
    // passive  : if locked, restore accuracy + speed to locked values every tick
    //            so no external modifier can permanently change them.

    window.abilities.onMissed['FD9FBCB0-B513-4685-B9F4-1906FE92200B'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        if (!card || card._vFirstAttackSeen) { return; }
        card._vFirstAttackSeen = true;

        card._vLockedAccuracy = Number(card.accuracy) || 0;
        card._vLockedSpeed    = Number(card.speed)    || 0;
        card._vLocked         = true;

        effects.add(card, ['powerup']);
        card.activeTraits.premonition = { label: 'Premonition \u2605 ACC ' + card._vLockedAccuracy + ' | SPD ' + card._vLockedSpeed + ' locked', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Premonition: opponent's first attack missed \u2014 accuracy and speed are now locked!", 'ability', { cardId: card.id, lockedAccuracy: card._vLockedAccuracy, lockedSpeed: card._vLockedSpeed });
        }
    };

    window.abilities.damaged['FD9FBCB0-B513-4685-B9F4-1906FE92200B'] = function(team1, _team2, index1) {
        var card = team1[index1];
        if (card) { card._vFirstAttackSeen = true; } // first attack hit — no lock
    };

    window.abilities.passive['FD9FBCB0-B513-4685-B9F4-1906FE92200B'] = function(card) {
        if (!card || !card._vLocked) { return; }
        card.accuracy = card._vLockedAccuracy;
        card.speed    = card._vLockedSpeed;
    };

    // ─── Turok — Stamina Dominance ───────────────────────────────────────────────
    // passive: while card.currentStamina > opponent.currentStamina, pAtk is boosted
    // by 8% over baseline. Reverts to base the moment the condition is no longer true.

    window.abilities.passive['DBA5A9F0-2BCE-4BDA-95D1-7CFD2958E7E0'] = function(card, _myIndex, _myTeam, oppTeam, oppIndex) {
        if (card._basePAtk === undefined) { card._basePAtk = Number(card.pAtk) || 0; }

        var opp = oppTeam && oppTeam[oppIndex];
        var myStamina  = Number(card.currentStamina) || 0;
        var oppStamina = opp ? (Number(opp.currentStamina) || 0) : 0;

        if (myStamina > oppStamina) {
            var boosted = Math.round(card._basePAtk * 1.08);
            if (card.pAtk !== boosted) {
                var _origTurokPAtk = card.pAtk;
                card.pAtk = boosted;
                window.flashStatGlow(card, 'pAtk', _origTurokPAtk, boosted);
                effects.add(card, ['powerup']);
            }
            card.activeTraits.staminaDominance = { label: 'Stamina Dominance ★ +8% pAtk (' + myStamina + ' vs ' + oppStamina + ')', turnsLeft: 9999 };
        } else {
            if (card.pAtk !== card._basePAtk) { card.pAtk = card._basePAtk; }
            delete card.activeTraits.staminaDominance;
        }
    };

    // ─── Vader/Dark Howl — Dark Howl ─────────────────────────────────────────────
    // move: 15% chance each turn to steal all of the opponent's damage output.
    // Vader absorbs the opponent's full current pAtk + mAtk on top of his own
    // baseline. The opponent is stripped back to their base attack stats.
    // Baselines for both cards are snapshotted on the first move tick.
    // The steal re-rolls every turn — successive steals refresh Vader's bonus
    // and keep the opponent pinned at their base.

    window.abilities.move['87C02D87-5247-40DC-8927-8F04313FB010'] = function(team1, team2, index1, index2, _events, logFn) {
        var card = team1[index1];
        var opp  = team2[index2];
        if (!opp || opp.hp <= 0) { return; }

        // Snapshot baselines once
        if (card._darkHowlBasePAtk === undefined) { card._darkHowlBasePAtk = Number(card.pAtk) || 0; }
        if (card._darkHowlBaseMAtk === undefined) { card._darkHowlBaseMAtk = Number(card.mAtk) || 0; }
        if (opp._darkHowlBasePAtk  === undefined) { opp._darkHowlBasePAtk  = Number(opp.pAtk)  || 0; }
        if (opp._darkHowlBaseMAtk  === undefined) { opp._darkHowlBaseMAtk  = Number(opp.mAtk)  || 0; }

        if (Math.random() >= 0.15) { return; }

        var stolenPAtk = Number(opp.pAtk) || 0;
        var stolenMAtk = Number(opp.mAtk) || 0;

        // Vader absorbs opponent's full current attack output on top of own base
        card.pAtk = card._darkHowlBasePAtk + stolenPAtk;
        card.mAtk = card._darkHowlBaseMAtk + stolenMAtk;

        // Opponent stripped back to base
        opp.pAtk = opp._darkHowlBasePAtk;
        opp.mAtk = opp._darkHowlBaseMAtk;

        effects.add(card, ['powerup']);
        effects.add(opp, ['shakeY']);
        card.activeTraits.darkHowl = { label: 'Dark Howl \u2605 +' + stolenPAtk + ' pAtk | +' + stolenMAtk + ' mAtk absorbed', turnsLeft: 9999 };
        opp.activeTraits.darkHowl  = { label: 'Damage Stolen by ' + card.name, turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Dark Howl: absorbed " + stolenPAtk + " pAtk + " + stolenMAtk + " mAtk from " + opp.name + "!", 'ability', { cardId: card.id, targetId: opp.id, stolenPAtk: stolenPAtk, stolenMAtk: stolenMAtk });
        }
    };

    // ─── Vigil — Iron Mind ───────────────────────────────────────────────────────
    // passive: stamp _vigilMentalImmune every tick (traits.js guard uses this flag).
    // Main:    each tick a mental trait was blocked → +10 ward (permanent accumulation).
    // Variant: each tick a mental trait was blocked → increment mAtk stack (cap 2);
    //          +400 mAtk per stack. Resets to base the turn no mental is blocked.

    window.abilities.passive['EF45DF89-3690-4B5E-A839-71894AD789B1'] = function(card) {
        if (!card) { return; }
        if (card._vigilBaseMAtk === undefined) { card._vigilBaseMAtk = Number(card.mAtk) || 0; }

        // Always stamp immune flag so traits.js guard is active
        card._vigilMentalImmune = true;

        if (card._vigilMentalBlockedThisTick) {
            card._vigilMentalBlockedThisTick = false;

            // Main: +10 ward per blocked turn
            var _origVigilWard = Number(card.ward) || 0;
            card.ward = _origVigilWard + 10;
            window.flashStatGlow(card, 'ward', _origVigilWard, card.ward);

            // Variant: +400 mAtk per blocked turn, capped at 2 stacks
            card._vigilMentalTurnStacks = Math.min((card._vigilMentalTurnStacks || 0) + 1, 2);
            var _origVigilMAtk = Number(card.mAtk) || 0;
            card.mAtk = card._vigilBaseMAtk + (card._vigilMentalTurnStacks * 400);
            window.flashStatGlow(card, 'mAtk', _origVigilMAtk, card.mAtk);

            effects.add(card, ['powerup']);
            card.activeTraits.ironMind = {
                label: 'Iron Mind \u2605 Mental Blocked \u2192 +10 ward | +' + (card._vigilMentalTurnStacks * 400) + ' mAtk (\u00d7' + card._vigilMentalTurnStacks + ')',
                turnsLeft: 9999
            };
        } else {
            // No mental blocked this tick — reset mAtk stacks
            card._vigilMentalTurnStacks = 0;
            if (card.mAtk !== card._vigilBaseMAtk) { card.mAtk = card._vigilBaseMAtk; }
            delete card.activeTraits.ironMind;
        }
    };

    // ─── Vindicor — Retribution ───────────────────────────────────────────────────
    // damaged: fires when Vindicor takes damage. If the hit was a critical, add
    // +600 mAtk (main) and +600 pAtk (variant) — permanent, cumulative per crit.

    window.abilities.onCrit['FDA97292-B9B6-47CB-9FB2-3D47E93ADFA8'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        if (!card) { return; }

        var _origVindicorMAtk = Number(card.mAtk) || 0;
        var _origVindicorPAtk = Number(card.pAtk) || 0;
        card.mAtk = _origVindicorMAtk + 600;
        card.pAtk = _origVindicorPAtk + 600;
        window.flashStatGlow(card, 'mAtk', _origVindicorMAtk, card.mAtk);
        window.flashStatGlow(card, 'pAtk', _origVindicorPAtk, card.pAtk);

        effects.add(card, ['powerup']);
        card.activeTraits.retribution = { label: 'Retribution \u2605 +' + card.mAtk + ' mAtk | +' + card.pAtk + ' pAtk (stacking)', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Retribution: crit landed \u2014 +600 mAtk, +600 pAtk! (now " + card.mAtk + " mAtk / " + card.pAtk + " pAtk)", 'ability', { cardId: card.id, newMAtk: card.mAtk, newPAtk: card.pAtk });
        }
    };

    // ─── Winston — Iron Resilience ────────────────────────────────────────────────
    // 3 successful attacks in a row repair 20% of base armor (capped at base).
    // After repair, the streak resets — another 3 hits needed for the next repair.
    //
    // move   : restore armor if ward-strip was queued (not needed here — no ward
    //          mechanic); reset streak if last turn's attack missed.
    // onHit  : increment consecutive hit counter; trigger repair at exactly 3.

    window.abilities.move['564B2413-6A08-477E-9331-173BE56AD193'] = function(team1, _team2, index1) {
        var card = team1[index1];
        if (!card) { return; }
        // Reset streak if the previous turn ended without a hit
        if (!card._winstonHitThisTurn && (card._winstonConsecHits || 0) > 0) {
            card._winstonConsecHits = 0;
            delete card.activeTraits.winstonStreak;
        }
        card._winstonHitThisTurn = false;
    };

    window.abilities.onHit['564B2413-6A08-477E-9331-173BE56AD193'] = function(team1, _team2, index1, _index2, _events, logFn) {
        var card = team1[index1];
        if (!card) { return; }

        // Snapshot base armor once
        if (card._winstonBaseArmor === undefined) { card._winstonBaseArmor = Number(card.armor) || 0; }

        card._winstonHitThisTurn = true;
        card._winstonConsecHits = (card._winstonConsecHits || 0) + 1;

        if (card._winstonConsecHits >= 3) {
            var repairAmount = Math.round(card._winstonBaseArmor * 0.20);
            card.armor = Math.min((Number(card.armor) || 0) + repairAmount, card._winstonBaseArmor);
            card._winstonConsecHits = 0; // reset — next repair needs another 3 hits
            delete card.activeTraits.winstonStreak;

            effects.add(card, ['powerup']);
            card.activeTraits.ironResilience = { label: 'Iron Resilience \u2605 3-hit streak \u2192 +' + repairAmount + ' armor repaired', turnsLeft: 9999 };
            if (typeof logFn === 'function') {
                logFn(card.name + "'s Iron Resilience: 3-hit streak \u2014 repaired " + repairAmount + " armor! (now " + card.armor + ")", 'ability', { cardId: card.id, repairAmount: repairAmount, newArmor: card.armor });
            }
        } else {
            card.activeTraits.winstonStreak = { label: 'Iron Resilience: ' + card._winstonConsecHits + '/3 hits', turnsLeft: 9999 };
        }
    };

    // ─── Xenomorph — Predator Strike ──────────────────────────────────────────────
    // onMiss: when Xenomorph misses and the opponent is below 50% HP,
    // the miss still deals 30% of Xenomorph's pAtk as direct damage (ignores armor).

    window.abilities.onMiss['182EC655-D954-47FA-8347-EEEB398F6CE8'] = function(team1, team2, index1, index2, _events, logFn) {
        var card = team1[index1];
        var opp  = team2[index2];
        if (!opp || opp.hp <= 0) { return; }

        var oppMaxHp = opp.originalHp || opp._maxHp || opp.hp;
        if (opp.hp >= oppMaxHp * 0.50) { return; } // only active while opponent < 50% HP

        var missDamage = Math.round((Number(card.pAtk) || 0) * 0.30);
        if (missDamage <= 0) { return; }

        opp.hp = Math.max(0, opp.hp - missDamage);

        effects.add(card, ['powerup']);
        effects.add(opp, ['shakeY']);
        card.activeTraits.predatorStrike = { label: 'Predator Strike \u2605 Miss \u2192 ' + missDamage + ' dmg (opp <50% HP)', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Predator Strike: missed but dealt " + missDamage + " damage to " + opp.name + "! (" + opp.hp + " HP remaining)", 'ability', { cardId: card.id, targetId: opp.id, missDamage: missDamage });
        }
    };

    // ─── Zero — Frozen Fury ───────────────────────────────────────────────────────
    // passive: while Zero has the slowed trait, pAtk and mAtk are boosted by 20%
    // over baseline. Reverts to base the moment slowed clears.

    window.abilities.passive['362248BC-20E8-4DCE-B927-1E5F47C0A8D0'] = function(card) {
        if (!card) { return; }
        if (card._zeroBasePAtk === undefined) { card._zeroBasePAtk = Number(card.pAtk) || 0; }
        if (card._zeroBaseMAtk === undefined) { card._zeroBaseMAtk = Number(card.mAtk) || 0; }

        var isSlowed = window.traits && traits.has(card, 'slowed');

        if (isSlowed) {
            var boostedPAtk = Math.round(card._zeroBasePAtk * 1.20);
            var boostedMAtk = Math.round(card._zeroBaseMAtk * 1.20);
            if (card.pAtk !== boostedPAtk || card.mAtk !== boostedMAtk) {
                var _origZeroPAtk = card.pAtk;
                var _origZeroMAtk = card.mAtk;
                card.pAtk = boostedPAtk;
                card.mAtk = boostedMAtk;
                window.flashStatGlow(card, 'pAtk', _origZeroPAtk, boostedPAtk);
                window.flashStatGlow(card, 'mAtk', _origZeroMAtk, boostedMAtk);
                effects.add(card, ['powerup']);
            }
            card.activeTraits.frozenFury = { label: 'Frozen Fury \u2605 Slowed \u2192 +20% dmg', turnsLeft: 9999 };
        } else {
            if (card.pAtk !== card._zeroBasePAtk) { card.pAtk = card._zeroBasePAtk; }
            if (card.mAtk !== card._zeroBaseMAtk) { card.mAtk = card._zeroBaseMAtk; }
            delete card.activeTraits.frozenFury;
        }
    };

    // ─── Cannus — Plague Strike ───────────────────────────────────────────────────
    // onCrit   : 60% chance — extend every negative-category trait on the opponent
    //            by +1 turn. Negative categories: dot, control, disruption, mental, defence.
    // onDefeated: when Cannus is killed, all negative traits currently on Cannus are
    //            applied fresh to the opponent (using traits.apply — respects immunity,
    //            refreshes duration if opponent already has the trait).

    var _cannusNegCats = { dot: true, control: true, disruption: true, mental: true, defence: true };

    window.abilities.onCrit['8601F3E4-4EC5-4E31-B618-F47CE3563ED2'] = function(team1, team2, index1, index2, _events, logFn) {
        if (Math.random() >= 0.60) { return; }
        var card = team1[index1];
        var opp  = team2[index2];
        if (!opp || opp.hp <= 0 || !opp.activeTraits) { return; }

        var defs = window.traits && traits.definitions;
        var extended = [];

        for (var traitName in opp.activeTraits) {
            var def = defs && defs[traitName];
            var entry = opp.activeTraits[traitName];
            if (def && _cannusNegCats[def.category] && entry.turnsLeft < 9999) {
                entry.turnsLeft += 1;
                extended.push(def.label || traitName);
            }
        }

        if (extended.length === 0) { return; }

        effects.add(card, ['powerup']);
        effects.add(opp, ['shakeY']);
        card.activeTraits.plagueStrike = { label: 'Plague Strike \u2605 Crit \u2192 +1 turn on ' + extended.length + ' effect(s)', turnsLeft: 9999 };
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Plague Strike: crit extended [" + extended.join(', ') + "] by 1 turn on " + opp.name + "!", 'ability', { cardId: card.id, targetId: opp.id, extended: extended });
        }
    };

    window.abilities.onDefeated['8601F3E4-4EC5-4E31-B618-F47CE3563ED2'] = function(team1, team2, index1, index2, _excess, _events, logFn) {
        var card = team1[index1]; // Cannus (defeated)
        var opp  = team2[index2]; // the attacker
        if (!opp || opp.hp <= 0 || !card.activeTraits) { return; }

        var defs = window.traits && traits.definitions;
        var transferred = [];

        for (var traitName in card.activeTraits) {
            var def = defs && defs[traitName];
            if (!def || !_cannusNegCats[def.category]) { continue; }
            try { traits.apply(opp, traitName); } catch(e) {}
            transferred.push(def.label || traitName);
        }

        if (transferred.length === 0) { return; }

        effects.add(opp, ['shakeY']);
        if (typeof logFn === 'function') {
            logFn(card.name + "'s Plague Strike: on death, transferred [" + transferred.join(', ') + "] to " + opp.name + "!", 'ability', { cardId: card.id, targetId: opp.id, transferred: transferred });
        }
    };

})();
